import { z } from 'zod';
import { CARD_MAX_LEVEL } from '../inventory/cards';
import { PVP_RUNE_WIRE_KINDS, pvpAppearanceSchema, pvpNicknameSchema } from './appearance';
import { pvpCombatProfileSchema } from './combat-profile';
import { pvpHealthMilliSchema } from './health';
import { PVP_MODES } from './modes';
import {
  PVP_ABANDON_CAUSES,
  PVP_CLOSE_REASONS,
  PVP_END_REASONS,
  PVP_MATCH_STATES,
  PVP_SEATS,
} from './states';

/**
 * What crosses the duel socket, in both directions.
 *
 * ## The browser sends INTENT, never state
 *
 * There is no position, no velocity, no health, no hit, no kill and no damage
 * number in the client-to-server schema, and there must never be one. A duel
 * where the loser's browser can describe the world is not a duel. What a player
 * may say is: which way I am holding, where I am pointing, and whether I am
 * holding fire. Everything else is the room's to decide.
 *
 * ## Short field names, on purpose
 *
 * This is the one schema in the repository that is sent twenty times a second.
 * `p` instead of `position` is not premature optimisation here; at 20 Hz the
 * key names ARE a measurable fraction of the frame. The verbose names live in
 * the TypeScript types, where they cost nothing.
 *
 * A binary encoding would be smaller again and is deliberately NOT done yet:
 * JSON is inspectable, and the first version of an authoritative protocol
 * should be one a human can read in a network tab while proving it correct.
 * `docs/adr` records that as a known, measured limit rather than an oversight.
 */

/* -------------------------------------------------------------------------- */
/*  Limits                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The largest command frame the room will even parse.
 *
 * Checked on the RAW message before `JSON.parse`, because parsing is the
 * expensive step and a megabyte of nonsense must cost the room a length check
 * rather than a parse. A legitimate command is well under 200 bytes.
 */
export const PVP_MAX_COMMAND_BYTES = 512;

/**
 * Commands accepted per second, per connection, before the room starts dropping.
 *
 * A 60 fps client that sends one command per frame sends 60; the allowance is
 * above that so an honest fast machine is never throttled, and far below what a
 * script can produce. Dropping is silent and counted - a rejected command is
 * not an error the player did anything about.
 */
export const PVP_MAX_COMMANDS_PER_SECOND = 90;

/**
 * Sustained abuse, measured in dropped commands, before the socket is closed.
 *
 * One burst is a lag spike. A thousand is a script, and a script that keeps a
 * socket open is a script that keeps a room's CPU busy.
 */
export const PVP_RATE_LIMIT_CLOSE_THRESHOLD = 600;

/**
 * How much unsent data a connection may hold before the room stops adding to it.
 *
 * A client that opens a socket and never reads is not a protocol error - it is
 * a slow reader, and the runtime answers by queueing. Queueing 20 frames a
 * second at a reader that takes none is a memory leak with a person's name on
 * it, and the room shares its memory with the OTHER player.
 *
 * So a backlogged connection is skipped for the frames that are safe to skip:
 * a `state` frame is a photograph of a world the next one re-photographs 50 ms
 * later, and missing one costs a stale sprite. `welcome`, `ended`, `error` and
 * `closing` are NEVER skipped - each is said once and is the only time it is
 * said.
 *
 * What this deliberately does not do is change the tick. The simulation
 * advances exactly the same way for a backlogged room as for a healthy one -
 * throttling the TELLING is presentation, throttling the world would be one
 * player's slow connection deciding the other player's duel.
 *
 * 64 KiB is roughly forty state frames of a busy arena: far past any honest
 * jitter, far short of a heap somebody notices.
 */
export const PVP_MAX_BUFFERED_BYTES = 64 * 1024;

/**
 * The widest raw aim vector a client may send.
 *
 * The vector is normalised by the server, so its LENGTH carries no information
 * and only its direction survives. The bound exists so the normalisation cannot
 * be handed a value that overflows the integer maths - not because a longer
 * vector would aim further.
 */
export const PVP_MAX_AIM_COMPONENT = 1_000_000;

/**
 * The widest coordinate this protocol will parse, in milli-units.
 *
 * A bound on the SCHEMA, not on the arena: the room refuses anything outside
 * its own walls anyway, and this exists so a hostile value cannot reach the
 * integer maths at all. Generous enough that no arena this protocol describes
 * could outgrow it, tight enough that it is not a number.
 */
export const PVP_MAX_ARENA_MU = 100_000_000;

/**
 * How far ahead of the last APPLIED sequence a command may claim to be.
 *
 * The schema alone allows anything up to `Number.MAX_SAFE_INTEGER`, and a
 * client that sends that number once has permanently silenced itself: every
 * later command is at or below it and is dropped as a replay. That is only
 * self-harm, but it is self-harm the room can refuse instead of honour, and
 * refusing it keeps "the sequence is a counter" true rather than "the sequence
 * is a counter unless somebody sets it to the end".
 *
 * Ten thousand is far past any honest gap. A connection is capped at
 * {@link PVP_MAX_COMMANDS_PER_SECOND}, so this is nearly two minutes of input
 * lost to packet loss before an honest client could reach it - longer than a
 * duel is allowed to last.
 */
export const PVP_MAX_SEQUENCE_SKIP = 10_000;

/* -------------------------------------------------------------------------- */
/*  Client -> server                                                          */
/* -------------------------------------------------------------------------- */

/** One axis of the movement intent. Three values, and no fourth. */
const axisSchema = z.union([z.literal(-1), z.literal(0), z.literal(1)]);

/**
 * **A projecção de uma mira: de onde a nave está para onde se aponta.**
 *
 * ## Porque ela vive aqui e não no cliente
 *
 * Ela era do `apps/web`, porque foi o cliente que precisou dela primeiro. Mas o
 * que ela descreve é o CONTRATO da mira — a forma exacta em que uma intenção
 * chega à sala — e desde o gate V28I.BW.5C há um segundo consumidor: o piloto
 * determinístico da bancada de integração, que tem de apontar exactamente como o
 * cliente aponta ou não estaria a provar o cliente.
 *
 * Duas cópias divergiriam no dia em que uma fosse corrigida. Uma só não pode.
 *
 * ## A escala, e porque ela não é normalização
 *
 * Um quadrante tem milhões de milli-unidades de lado, e uma diferença crua
 * transborda o limite que a sala põe numa componente de mira. Dividir pela maior
 * componente preserva o ângulo exacto e não pode transbordar. O comprimento não
 * significa nada: a sala normaliza, e apontar mais longe não atira mais longe.
 *
 * Devolve `null` quando não há direcção — o ponteiro em cima do casco, ou uma
 * diferença tão pequena que a truncatura a colapsa.
 */
export function pvpAimVector(
  shipX: number,
  shipY: number,
  worldX: number,
  worldY: number,
): { readonly ax: number; readonly ay: number } | null {
  const dx = worldX - shipX;
  const dy = worldY - shipY;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
    return null;
  }
  if (dx === 0 && dy === 0) {
    return null;
  }
  const scale = PVP_MAX_AIM_COMPONENT / Math.max(Math.abs(dx), Math.abs(dy));
  const ax = Math.trunc(dx * scale);
  const ay = Math.trunc(dy * scale);
  if (ax === 0 && ay === 0) {
    /*
     * A truncatura colapsou-a. Só alcançável para uma diferença muito abaixo de
     * uma milli-unidade, que é um ponteiro em cima do casco por outro nome.
     */
    return null;
  }
  return { ax, ay };
}

const aimComponentSchema = z.number().int().min(-PVP_MAX_AIM_COMPONENT).max(PVP_MAX_AIM_COMPONENT);

/**
 * The one command a playing client sends.
 *
 * `.strict()` like every authoritative request in this API: a field nobody
 * declared is a field nobody validated, and the first thing an attacker tries
 * is adding one.
 */
export const pvpCommandSchema = z
  .object({
    t: z.literal('cmd'),
    /**
     * Strictly increasing, per connection.
     *
     * The room keeps the highest it has seen and drops anything at or below it.
     * That is what makes a retry harmless and a replay useless: an old frame
     * cannot walk the ship backwards, because an old frame is not applied.
     */
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    /** WASD reduced to two axes. Not a speed - the room owns speed. */
    mx: axisSchema,
    my: axisSchema,
    /** Where the player is pointing. Direction only; the room normalises it. */
    ax: aimComponentSchema,
    ay: aimComponentSchema,
    /** Is the fire button held. Cadence is the room's, not the client's. */
    f: z.boolean(),
    /**
     * A REQUEST to teleport to a chosen place. Never a result.
     *
     * ## Why coordinates are allowed here at all
     *
     * The first version was a boolean, and it produced a dash: the ship moved
     * a fixed distance along its aim. The design asks for something else - the
     * player arms a mode, picks an area on the map, and the server decides
     * whether they may go there. That picture cannot be drawn from a boolean,
     * so the request carries where the player CLICKED.
     *
     * ## Why that is still not the client deciding anything
     *
     * These two numbers are an argument to a server function, in exactly the
     * way `ax`/`ay` already are. The room validates the point against the
     * arena, the obstacles, the hull's own radius, the charge, the player's
     * life, the generation and the sequence, and answers with a position IT
     * chose. A refused destination moves nothing and spends nothing. What the
     * client may never do is state where it ENDED UP, and no field here lets
     * it.
     *
     * Milli-units, integral, and bounded by the largest arena this protocol
     * describes - a value outside that is refused by the schema rather than
     * clamped into the map.
     */
    tp: z
      .object({
        x: z.number().int().min(0).max(PVP_MAX_ARENA_MU),
        y: z.number().int().min(0).max(PVP_MAX_ARENA_MU),
      })
      .strict()
      .optional(),
    /**
     * A ordem de DETONAR as marcas de sangramento (gate V28G.N).
     *
     * ## O que ela pode dizer, e é tudo
     *
     * Que o jogador carregou no botão. Não há alvo, não há contagem, não há
     * magnitude e não há lista - a autoridade decide sozinha o que rebenta, de
     * quem, sobre quem e por quanto, a partir de um estado que o cliente não
     * escreve. Um campo de alvo aqui seria a primeira coisa que um atacante
     * tentava; não existe.
     *
     * ## Porque um booleano opcional, e não um contador
     *
     * Porque é um LATCH e não um estado sustentado: a sala arma a ordem e o tique
     * seguinte consome-a. A maior parte dos frames não a traz, exatamente como a
     * maior parte não traz `tp`, e um campo ausente diz "não pedi nada".
     *
     * Sem marcas, é um `no-op` seguro: nada acontece, nada é gasto, nada é dito.
     */
    bd: z.boolean().optional(),
    /**
     * **A ORDEM de ligar uma carta** (gate V28H.J).
     *
     * ## Porque viaja no comando, e não numa rota
     *
     * Porque é um acto dentro de uma partida a decorrer, e uma partida a
     * decorrer vive na sala. A caça manda a sua ordem por HTTP porque a caça é
     * uma sessão com transações; um duelo tem um socket já ligado a UMA cadeira
     * por um bilhete assinado de uso único, e é esse socket que diz QUEM.
     *
     * ## Porque não há campo para dizer de quem é o espaço
     *
     * Porque é a defesa inteira: a sala deriva o participante da ligação em que
     * a ordem chegou, e não existe campo onde nomear outra pessoa. Um jogador
     * não pode pedir para ligar a carta do adversário porque não há como o
     * dizer - e não porque alguém se lembrou de verificar.
     *
     * ## O que ela diz, e é tudo
     *
     * O ESPAÇO - 1, 2 ou 3 - e a identidade da ordem. Não há carta, não há
     * duração, não há arrefecimento e não há alvo: tudo isso é decidido pela
     * autoridade a partir do loadout que ela própria congelou quando o piloto se
     * sentou.
     *
     * ## Porque a identidade da ordem existe ao lado de `seq`
     *
     * `seq` já protege contra a repetição de um FRAME: a sala guarda o mais
     * alto que viu e larga tudo o que vem abaixo dele. O que ele não cobre é o
     * cliente a reenviar a MESMA ordem num frame novo - o que um cliente honesto
     * faz quando não vê a carta ligar e insiste. `id` faz a segunda tentativa
     * ser lida em vez de ligar outra vez, e é o mesmo papel do `commandId` da
     * caça.
     */
    ck: z
      .object({
        /** O espaço: 1, 2 ou 3. Nunca uma carta, e nunca outro participante. */
        s: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        /** A identidade desta ordem, repetida em cada tentativa. */
        id: z.string().uuid(),
      })
      .strict()
      .optional(),
    /**
     * **A ordem de SOLTAR uma carga** (gate V28H.N).
     *
     * ## Porque uma segunda ordem, e não uma duração
     *
     * Porque a Lança Solar é carregada enquanto a tecla está premida, e é
     * contrato aprovado. O instante em que a tecla se solta é uma decisão do
     * jogador; sob autoridade do servidor, uma decisão que não chega cá não
     * existe. Uma carga de duração fixa dispararia sozinha e tiraria à carta a
     * única decisão que ela tem.
     *
     * Tem exactamente a forma de `ck`, e pelas mesmas razões: o ESPAÇO e a
     * identidade da ordem, sem campo onde nomear outra pessoa. Quem não solta
     * chega ao fim da carga sozinho, e ela vale cheia.
     */
    /**
     * **A ordem de PILOTAR o que este casco está a tocar** (gate V28H.X).
     *
     * ## Porque ela existe
     *
     * O embarque acontecia SOZINHO, ao encostar. Isso torna a estrutura uma
     * armadilha: quem passa ao lado do Leviatã de outra pessoa fica preso lá
     * dentro treze segundos sem ter pedido nada. O dono decidiu que há um botão,
     * e um botão é uma ordem.
     *
     * ## Porque não diz QUAL
     *
     * Porque não é preciso, e um campo a mais é uma superfície a mais: a sala
     * escolhe a estrutura que este casco está a tocar - se houver duas, a mais
     * próxima, e um empate fica com a primeira da ordem canónica. O cliente não
     * pode nomear uma que ele não esteja a tocar, porque não há onde a nomear.
     *
     * Latch e não hold, como a detonação: uma ordem vale um tique. Repetir a
     * mesma identidade é lido em vez de embarcar outra vez.
     */
    bo: z
      .object({
        /** A identidade desta ordem, repetida em cada tentativa. */
        id: z.string().uuid(),
      })
      .strict()
      .optional(),
    cr: z
      .object({
        /** O espaço: 1, 2 ou 3. */
        s: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        /** A identidade desta ordem, repetida em cada tentativa. */
        id: z.string().uuid(),
      })
      .strict()
      .optional(),
  })
  .strict();

export type PvpCommand = z.infer<typeof pvpCommandSchema>;

/**
 * The client declaring it has finished loading and is ready to fight.
 *
 * Separate from the first command so a client that is still building textures
 * cannot start a countdown it will spend standing still.
 */
export const pvpReadySchema = z.object({ t: z.literal('ready') }).strict();

/**
 * The player choosing to abandon the match they are in (ADR 0043).
 *
 * ## Why this is a message and not a route
 *
 * Because it is an act inside a running match, and a running match lives in the
 * room. `POST /api/pvp/team/leave` exists and refuses this on purpose - "the
 * fight has begun; leaving is the room's business, not the lobby's" - so it
 * dissolves a lobby and can never end a fight. Adding a second HTTP door into a
 * live room would be a door the room does not hold the lock for.
 *
 * ## Why it carries nothing
 *
 * There is no participant, no team, no match, no winner and no result in it,
 * and that is the whole security argument. The connection is already bound to
 * one chair by a signed, one-use ticket, so the room derives WHO from the
 * socket it arrived on. A client cannot name another player because there is no
 * field in which to name one.
 *
 * ## Why it is distinct from a disconnection
 *
 * A dropped socket is not a decision: F5, a lid, a proxy, a lost frame. Those
 * keep the reconnection grace and the chair, exactly as before. This is a
 * deliberate press of a button, so it does not wait out a window that exists to
 * protect somebody who never chose to go.
 *
 * Idempotent by construction: a chair that has already left has already left,
 * and repeating it is free.
 */
export const pvpLeaveSchema = z.object({ t: z.literal('leave') }).strict();

export const pvpClientFrameSchema = z.discriminatedUnion('t', [
  pvpCommandSchema,
  pvpReadySchema,
  pvpLeaveSchema,
]);

export type PvpClientFrame = z.infer<typeof pvpClientFrameSchema>;

/* -------------------------------------------------------------------------- */
/*  Server -> client                                                          */
/* -------------------------------------------------------------------------- */

const seatSchema = z.enum(PVP_SEATS);

/**
 * Quantas serras um casco pode ter a orbitá-lo: **quatro** (gate V28G.P).
 *
 * Declarado AQUI e verificado contra `SAW_MAX_BLADES` numa prova do domínio,
 * pela mesma disciplina de {@link PVP_MAX_WIRE_POISON_TICKS}: `shared` não
 * conhece `game-domain`, e conhecer seria uma volta na direção das dependências.
 *
 * É um teto sobre a REPRESENTAÇÃO, e não sobre o que a joia concede: o nível 30
 * mantém quatro lâminas e não há nível 31, portanto uma quinta descreveria uma
 * órbita que nenhum nível produz.
 */
export const PVP_MAX_WIRE_SAW_BLADES = 4;

/**
 * O módulo da fase de uma órbita: **41 760** (gate V28G.P).
 *
 * `1 440 ms de período base x 29 degraus de velocidade`, declarado aqui e
 * verificado contra `SAW_ORBIT_PHASE_UNITS` na mesma prova, pela mesma razão.
 *
 * Um valor fora dele não pode ter sido produzido por nenhuma sequência de
 * tiques, e aceitá-lo seria desenhar lâminas num ângulo que a autoridade nunca
 * calculou.
 */
export const PVP_MAX_WIRE_SAW_PHASE = 41_760 - 1;

/** A ship, as the room sees it. Positions are integer milli-units. */
/**
 * **Os dois canhões temporários estão montados neste casco** (gate V28H.J).
 *
 * ## Porque o FACTO viaja, e a razão não
 *
 * Exatamente como a contagem das serras: derivá-lo exigiria saber que carta
 * está no espaço e em que nível, e isso vive no perfil congelado que **nunca
 * sai do servidor**. O que atravessa o fio é "esta nave tem os canhões" - e
 * nunca porquê, nem por quanto tempo, nem quanto falta para poder outra vez.
 *
 * ## Porque é público, ao contrário do arrefecimento
 *
 * Porque os dois lados TÊM de os desenhar: eles disparam contra quem está do
 * outro lado, e um adversário que não os visse levaria tiros de uma arma
 * invisível. O arrefecimento é outra coisa - é informação de decisão, viaja só
 * para o dono, e vive na metade privada da cadeira.
 *
 * Ausente quer dizer NÃO, que é o estado de todo o casco na maior parte do
 * tempo. Um zero explícito custaria bytes em todos os frames de todas as
 * partidas para dizer o que a ausência já diz.
 */
export const pvpSuperCannonsFlagSchema = z.literal(1);

/**
 * **Os dois FACTOS públicos que as cartas de temporizador produzem** (V28H.L).
 *
 * `fz` - congelado - e `bt` - inalvejável. Os dois são públicos e têm de ser,
 * pela mesma razão que os canhões temporários são: eles mudam o que acontece
 * a quem está do outro lado. Um adversário que não visse o gelo não perceberia
 * porque a nave parou de responder, e um que não visse o Bastião não perceberia
 * porque os tiros dele atravessam.
 *
 * O que NÃO é público é quanto FALTA de cada um: isso é informação de decisão e
 * viaja só para o dono, na metade privada da cadeira, com o resto dos
 * arrefecimentos.
 *
 * **E o Manto não tem bandeira nenhuma**, deliberadamente: um casco invisível é
 * OMITIDO do frame do adversário, e não marcado nele. Uma bandeira "estou
 * invisível" com uma posição ao lado seria uma posição que um cliente
 * modificado desenhava à mesma.
 *
 * Ausente quer dizer NÃO, que é o estado de todo o casco na maior parte do
 * tempo.
 */
export const pvpCardFlagSchema = z.literal(1);

/**
 * **Os três espaços de cartas de UMA cadeira, para o HUD dela** (gate V28H.J).
 *
 * ## Porque é privado
 *
 * Porque um arrefecimento é informação de DECISÃO. Que os canhões estão
 * montados é público - eles estão no ecrã a disparar -, mas quanto falta para
 * poderem voltar é uma coisa que o adversário não tem como ver, e mandar-lha
 * seria dar-lhe uma vantagem que o jogo não decidiu dar.
 *
 * ## Porque três, sempre
 *
 * Porque o HUD desenha três caixas, e uma lista que encolhesse obrigá-lo-ia a
 * adivinhar quais faltam. Um espaço vazio diz-se com `d: null`.
 *
 * ## O que ele NÃO leva
 *
 * A instância, o nível e o catálogo. O nome de exibição chega ao cliente porque
 * ele conhece o catálogo público das dezasseis cartas; a instância e o nível
 * são inventário, e um frame de duelo que os levasse seria um frame a decorar
 * um cofre.
 */
export const pvpCardSlotSchema = z
  .object({
    /** O espaço: 1, 2 ou 3, pela ordem em que a barra desenha. */
    s: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    /** A carta congelada neste espaço, ou `null` se ele está vazio. */
    d: z.string().min(1).nullable(),
    /** Milissegundos que a habilidade ainda tem. Zero = inactiva. */
    a: z.number().int().min(0),
    /** Milissegundos de arrefecimento que faltam. Zero = pronta. */
    c: z.number().int().min(0),
    /** O arrefecimento TOTAL desta carta, para o anel ter denominador. */
    ct: z.number().int().min(0),
    /**
     * **O nível desta carta** (gate V28H.Q).
     *
     * ## Porque ele passou a viajar, depois de não viajar
     *
     * Ele foi deliberadamente omitido: o nível é inventário, e um frame que o
     * levasse seria um frame a decorar um cofre. O que viajava era só o
     * RESULTADO dele - o arrefecimento total.
     *
     * O que essa decisão não previu foi a barra: ela mostra o nível da carta, e
     * sem o número o projector preenchia `1`. O dono viu "Nível 1" numa carta
     * de nível 6, e uma barra que mente é pior do que uma barra que se cala.
     *
     * A objecção original continua de pé onde ela nasceu - o cofre é PÚBLICO
     * quando viaja em `ships`, e nada disto viaja lá. Este objecto é a metade
     * PRIVADA da cadeira, construída por ligação, e já leva os arrefecimentos de
     * quem a recebe. O nível da minha própria carta é meu, e o adversário nunca
     * o vê - nem o id da instância, que continua a não sair daqui.
     *
     * Ausente num espaço vazio, que não tem carta para ter nível.
     */
    l: z.number().int().min(1).max(CARD_MAX_LEVEL).optional(),
  })
  .strict();

export const pvpCardSlotsSchema = z.array(pvpCardSlotSchema).length(3);

/**
 * Os três espaços VAZIOS.
 *
 * Uma constante e não um literal repetido: o frame exige exactamente três, e
 * escrever a lista à mão em cada sítio seria três oportunidades de a escrever
 * com dois. É também o estado de uma cadeira que não trouxe cartas nenhumas.
 */
/*
 * Uma FUNÇÃO e não uma constante congelada: o esquema pede um array mutável, e
 * um objecto partilhado entre fixtures seria um estado global disfarçado.
 */
export function emptyPvpCardSlots(): PvpCardSlotFrame[] {
  return [
    { s: 1 as const, d: null, a: 0, c: 0, ct: 0 },
    { s: 2 as const, d: null, a: 0, c: 0, ct: 0 },
    { s: 3 as const, d: null, a: 0, c: 0, ct: 0 },
  ];
}

export type PvpCardSlotFrame = z.infer<typeof pvpCardSlotSchema>;

/**
 * **Uma invocação, no fio** (gate V28H.L).
 *
 * O Drone e o Leviatã. Cinco factos e mais nada: o que é, onde está, para onde
 * aponta, de quem é, e quanto da vida dela resta.
 *
 * O que NÃO viaja: o tempo que lhe falta, o acumulador de disparo e quem a
 * pilota em segundos. O primeiro é informação de decisão de quem a invocou, e
 * os outros dois são mecânica que nenhuma cena lê. O piloto viaja - a cena tem
 * de saber que aquele casco está lá dentro para não o desenhar ao lado.
 *
 * A vida em basis points e não em milli: a barra desenha uma RAZÃO, e mandar
 * dois números de quarenta dígitos para calcular uma fracção seria pagar por
 * uma divisão que o servidor já sabe fazer.
 */
export const pvpSummonFrameSchema = z.object({
  i: z.number().int().min(0),
  /** `d` de drone, `l` de leviatã. Uma letra: há duas formas, e não mais. */
  k: z.union([z.literal('d'), z.literal('l')]),
  x: z.number().int().min(0).max(PVP_MAX_ARENA_MU),
  y: z.number().int().min(0).max(PVP_MAX_ARENA_MU),
  ax: z.number().int(),
  ay: z.number().int(),
  /** De quem ela é. A cena pinta-a com a cor da equipa dele. */
  p: z.string().min(1).max(32),
  /** A vida que resta, em basis points do máximo dela. */
  hp: z.number().int().min(0).max(10_000),
  /** Quem a pilota, ou ausente. Só o Leviatã. */
  pl: z.string().min(1).max(32).optional(),
});

export type PvpSummonFrame = z.infer<typeof pvpSummonFrameSchema>;

/**
 * **O que uma carta está a fazer neste casco, para a cena desenhar** (V28H.L).
 *
 * Uma lista de letras, e não uma bandeira por carta: as cartas visíveis já são
 * três e vão ser mais, e um campo por cada uma seria um campo novo - e um bump
 * de protocolo - por cada habilidade escrita.
 *
 * - `o` o Turbilhão está EQUIPADO - o círculo mágico passivo (gate V28H.O);
 * - `b` a serra do Turbilhão está a cortar;
 * - `l` o feixe da Lança Solar está a arder;
 * - `c` a Lança Solar está a carregar;
 * - `g` a Esfera Ígnea está a carregar, e a bola cresce na boca da nave;
 * - `d` o Arranque está a avançar - os cinco tiques do impulso;
 * - `h` a Sobremarcha está ligada, e o casco anda mais depressa;
 * - `k` o Manto Espectral está ligado;
 * - `w` o Malware acabou de ser lançado DESTE casco (gate V28H.W).
 *
 * ## Porque `w` é do lançador e não da vítima
 *
 * Porque são duas coisas diferentes. A vítima leva `mw`, que é um estado e dura
 * oito segundos; o lançador tem uma animação curta de hack no próprio casco, e o
 * que a acende é a carta dele estar activa. Uma letra só para as duas faria a
 * nave que lançou ficar com o símbolo da infecção que ela não tem.
 *
 * ## Porque `k` não denuncia ninguém
 *
 * Porque um casco encoberto **não é escrito** no frame de quem não é da equipa
 * dele - ele é omitido, e não marcado. Esta letra só chega a quem já recebia
 * aquele casco: o dono e os aliados. É a diferença entre dizer "esta nave está
 * invisível" a toda a gente e dizer "a tua nave está invisível" a quem a
 * pilota, e é a segunda que o contrato pede - o dono precisa de uma silhueta
 * para se controlar.
 *
 * ## Porque a carga são DUAS letras e não uma com um nome ao lado
 *
 * Porque as duas cargas desenham coisas diferentes: uma acende uma corona no
 * bico, a outra faz nascer uma esfera que incha à frente do casco. Uma letra só,
 * com o nome da carta noutro campo, seria o nome de uma carta no protocolo - e
 * este frame nunca soube o nome de nada. `cg` serve as duas: é a fracção já
 * cumprida, e é ela que dá o tamanho da bola e a intensidade da corona.
 *
 * ## Porque `o` não é como as outras três
 *
 * As outras dizem que algo está a ACONTECER; esta diz que algo está equipado.
 * O círculo mágico do Turbilhão é passivo por decisão do dono - ele existe pela
 * caçada ou pelo duelo inteiro - e é ele que comunica, a toda a gente, a área
 * que aquela nave vai ferir quando ligar. Sem esta letra o adversário só
 * descobria o alcance no instante em que já estava dentro dele.
 *
 * Ausente quer dizer NADA, que é o estado de quase todo o casco em quase todo o
 * tique.
 */
export const pvpCardVisualSchema = z.array(
  z.union([
    z.literal('o'),
    z.literal('b'),
    z.literal('l'),
    z.literal('c'),
    z.literal('g'),
    z.literal('d'),
    z.literal('h'),
    z.literal('k'),
    z.literal('w'),
    /** A Marca da Caveira acabou de sair deste casco (gate V28H.X). */
    z.literal('m'),
  ]),
);

export const pvpShipFrameSchema = z.object({
  s: seatSchema,
  /** Os dois canhões temporários, quando montados. Ausente = não. */
  sc: pvpSuperCannonsFlagSchema.optional(),
  /** Congelado por uma carta. Ausente = não (gate V28H.L). */
  fz: pvpCardFlagSchema.optional(),
  /**
   * **Este casco está infectado com MALWARE** (gate V28H.W). Ausente = não.
   *
   * A cura que ele receber vale metade. É um estado do CASCO e não um
   * acontecimento, portanto viaja em todos os frames enquanto durar - quem
   * chegar a meio, por reconexão ou como espectador, vê o símbolo na mesma.
   */
  mw: pvpCardFlagSchema.optional(),
  /**
   * **Quanta casca de gelo desenhar**, de um a três (gate V28H.W).
   *
   * Presente só enquanto o Congelamento durar, e derivado do tempo que FALTA -
   * um por cada segundo restante, com tecto em três. Uma esfera de quatro
   * segundos começa em três camadas e desce a duas e a uma enquanto derrete, e
   * quem olha lê quanto tempo o adversário ainda está preso.
   *
   * Do que falta e não do que foi sorteado, e é deliberado: o total teria de
   * viajar como estado novo em três fronteiras - frame, instantânea e restauro -
   * para dizer uma coisa que o tempo restante já diz melhor.
   */
  ft: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  /**
   * **Paralisia TOTAL**: este congelamento também trava o ataque (V28H.W).
   *
   * Trinta por cento das vezes, decidido pela sala. Ele existe porque as duas
   * paralisias se desenham de maneiras diferentes - a total leva uma estrela de
   * gelo por cima do casco - e a cena não tem como saber qual é: a diferença
   * está na magnitude de uma ranhura que ela nunca vê.
   */
  pz: pvpCardFlagSchema.optional(),
  /**
   * **Este casco está LENTO** (gate V28H.W): metade da velocidade. Ausente = não.
   *
   * `lt` e não `sl`, que seria a letra óbvia: no 3x3 ela já é o SLOT da cadeira,
   * e duas coisas com o mesmo nome no mesmo objecto são um erro de compilação
   * num protocolo e uma confusão em qualquer leitura dele.
   */
  lt: pvpCardFlagSchema.optional(),
  /**
   * **Este casco está em CURTO-CIRCUITO** (gate V28H.X). Ausente = não.
   *
   * A cadência dele está reduzida, e isso não tem mais nenhum sinal no frame:
   * um adversário a disparar mais devagar é uma coisa que só se nota ao fim de
   * vários tiros, e um duelo decide-se antes disso. É um estado do CASCO e
   * viaja em todos os frames enquanto durar.
   */
  cc: pvpCardFlagSchema.optional(),
  /**
   * **Este casco está MARCADO pela Caveira** (gate V28H.X). Ausente = não.
   *
   * O dano que ele causa vale menos. A carta chama-se "marca", e a marca é
   * literalmente o que ela produz - sem este campo, a única carta do catálogo
   * cujo nome descreve um desenho não desenhava nada.
   */
  sk: pvpCardFlagSchema.optional(),
  /** Inalvejável por uma carta: os tiros atravessam-no. Ausente = não. */
  bt: pvpCardFlagSchema.optional(),
  /** O que as cartas deste casco estão a desenhar. Ausente = nada. */
  cv: pvpCardVisualSchema.optional(),
  /**
   * **Quanto da carga já foi cumprido**, em por cento (gate V28H.O).
   *
   * Presente só enquanto `c` está na lista. Ele é PÚBLICO de propósito, e é a
   * excepção deliberada à regra de que "quanto falta" viaja na metade privada:
   * a Lança Solar prende quem a carrega, e o contrato aprovado diz que o
   * adversário vê a nave presa e tem tempo para sair da linha. Uma antecipação
   * que só o dono consegue ler não é uma antecipação - é um aviso para ninguém.
   *
   * Por cento e não basis points: é uma barra de brilho, e a diferença entre
   * 74,31 % e 74 % não existe em píxeis.
   */
  cg: z.number().int().min(0).max(100).optional(),
  /**
   * **Com que força o feixe está a arder**, em por cento (gate V28H.O).
   *
   * Presente só enquanto `l` está na lista. A espessura desenhada sai daqui, e
   * é a mesma graduação com que a sala decide quem o feixe apanha - portanto um
   * feixe fino é fino para quem o vê e para quem leva.
   */
  cw: z.number().int().min(0).max(100).optional(),
  x: z.number().int(),
  y: z.number().int(),
  /** Aim direction, already normalised. Drawn as rotation; never re-derived. */
  ax: z.number().int(),
  ay: z.number().int(),
  /**
   * A vida deste casco, em MILLI, como STRING DECIMAL (gate V28G.S.1).
   *
   * Era um inteiro de pontos, e isso truncava ate 0,999 do que a caca tinha:
   * nenhuma fonte legal de vida maxima produz pontos inteiros. String e nao
   * numero porque a progressao nao tem tecto - 2^40 pontos passam de
   * `Number.MAX_SAFE_INTEGER` em milli - e a barra desenha a razao exata entre
   * este numero e `combatProfile[participante].maxHealthMilli`.
   */
  hp: pvpHealthMilliSchema,
  /**
   * **Quantas SERRAS orbitam este casco agora** (gate V28G.P).
   *
   * ## Porque a contagem viaja, em vez de o cliente a derivar
   *
   * Porque derivá-la exigiria o nível da joia, e o nível vive no perfil
   * congelado que **nunca sai do servidor**. Uma cena que o recebesse seria uma
   * cena a decorar um inventário, que é exatamente o que
   * `jewel-presentation.test.ts` proíbe. O que viaja é o FACTO - há três lâminas
   * à volta desta nave - e nunca a razão pela qual há três.
   *
   * Ausente quer dizer NENHUMA, que é o estado de todo o casco sem esta joia.
   * Um zero explícito custaria bytes em todos os frames de todas as partidas
   * para dizer o que a ausência já diz.
   */
  sw: z.number().int().min(1).max(PVP_MAX_WIRE_SAW_BLADES).optional(),
  /**
   * A FASE autoritativa da órbita, em unidades de fase (gate V28G.P).
   *
   * Presente exatamente quando {@link sw} está, e a cena deriva dela a posição e
   * a rotação de cada lâmina - determinística, e igual em todos os clientes.
   *
   * ## Porque a fase e não as posições
   *
   * Porque a posição é derivável e a fase não. Quatro pares de coordenadas por
   * casco seriam oito inteiros vinte vezes por segundo para dizer o que um
   * inteiro já diz - e seriam oito inteiros que um cliente poderia ver
   * discordarem do casco a que pertencem, se um frame os juntasse de tiques
   * diferentes.
   */
  sf: z.number().int().min(0).max(PVP_MAX_WIRE_SAW_PHASE).optional(),
  /** Connected right now. A disconnected ship still exists and can be shot. */
  c: z.boolean(),
  /**
   * **Tiques de EMPURRÃO que faltam a este casco** (gate V28G.J).
   *
   * ## Porque o cliente tem de saber
   *
   * Porque ele prevê o seu próprio movimento. Um preditor que não soubesse do
   * bloqueio continuaria a andar localmente contra ele e depois seria corrigido
   * de volta a cada frame - a nave saltava para trás vinte vezes por segundo, que
   * é exatamente o que o §6 proíbe. Sabendo, ele simplesmente para de prever e
   * segue a posição autoritativa.
   *
   * É a mesma razão pela qual os tiques das runas viajam em vez de serem
   * contados localmente: o cliente pergunta, nunca decide.
   *
   * ## Porque OPCIONAL
   *
   * Zero é o estado normal - ninguém está a ser empurrado quase sempre - e a
   * ausência diz "obedece ao piloto", que é exato.
   *
   * Nunca carrega direção nem distância: para onde a nave vai é o que as
   * posições autoritativas mostram, e um cliente com a sua própria opinião sobre
   * isso teria uma segunda física.
   */
  kb: z.number().int().min(1).optional(),
});

/** A projectile in flight. No target id: duel shots never home. */
export const pvpProjectileFrameSchema = z.object({
  i: z.number().int().min(0),
  x: z.number().int(),
  y: z.number().int(),
  /** Owner, so the client can colour it. Damage is never the client's business. */
  s: seatSchema,
  /**
   * **Este projétil é uma BOMBA** (gate V28G.J).
   *
   * ## Porque viaja, quando a maioria das coisas não viaja
   *
   * Porque a cena não pode deduzi-lo. Uma bomba é desenhada de outra maneira -
   * outro corpo, outra trilha, outra rotação - e nada mais no frame o denuncia:
   * a posição é uma posição e o dono é um dono. Deduzi-lo pela velocidade seria
   * a cena a inferir mecânica a partir de dois frames consecutivos, e a errar em
   * todos os que chegam em atraso.
   *
   * ## Porque OPCIONAL, e o que a ausência significa
   *
   * "Não é uma bomba", exatamente. Quase todo o projétil de quase toda a partida
   * é um tiro comum, e escrever `b: false` em cada um deles seria pagar um campo
   * por projétil, vinte vezes por segundo, para dizer o normal.
   *
   * Nunca carrega magnitude, raio, nível nem joia: o que uma cena precisa de
   * saber é que aquilo é uma bomba, e o resto do que ela faz é decidido pela
   * autoridade e anunciado quando acontecer.
   */
  b: z.literal(true).optional(),
  /**
   * **E esta é a bomba de uma CARTA, e qual** (gate V28H.U).
   *
   * `f` é a Esfera Ígnea, `c` é o Catolé, `z` é a esfera do Congelamento e `t` é
   * a estrela da Lentidão (gate V28H.W). Ausente é um tiro comum ou a Bomba
   * Atômica da joia - que continua a marcar-se com `b` e a não mudar em nada.
   *
   * Viaja pela mesma razão que `b` viaja, e a razão está escrita em cima: a
   * cena não pode deduzi-lo. As três coisas são desenhadas de maneiras
   * diferentes, e adivinhá-lo pelo TAMANHO seria a cena a decidir a arte a
   * partir de um número de equilíbrio - e a trocá-la no dia em que alguém
   * afinasse o raio.
   */
  /**
   * **De que ESTRUTURA este tiro saiu** (gate V28H.X). Ausente = de um casco.
   *
   * §d§ é um Drone e §l§ é um Leviatã, e a cena precisa das duas coisas por
   * razões diferentes: a salva do Leviatã é vermelha néon - a cor de um tiro sai
   * da cadeira de quem o deu, e o piloto está escondido lá dentro - e só ela
   * dispara à velocidade de ataque de alguém, que é o que decide o tamanho
   * desenhado de um tiro.
   */
  sv: z.union([z.literal('d'), z.literal('l')]).optional(),
  cb: z
    .union([z.literal('f'), z.literal('c'), z.literal('z'), z.literal('t'), z.literal('s')])
    .optional(),
  /**
   * **O raio da explosão que ela vai fazer**, em unidades de mundo.
   *
   * Presente só com `cb`, e existe por uma razão que a Bomba não tem: a Esfera
   * Ígnea CRESCE com a carga. Um corpo de tamanho fixo mostraria a mesma bola
   * para um toque e para uma carga cheia, e "adversários enxergam o tamanho
   * real da esfera" foi pedido por escrito - este campo é o que o cumpre.
   *
   * O raio da EXPLOSÃO e não o do corpo: é ele que a autoridade possui, o
   * corpo desenhado é uma fracção dele, e o que se vê passa assim a ser o que
   * apanha. Nunca carrega dano, nível, carta nem dono.
   */
  br: z.number().int().min(0).optional(),
});

/**
 * **Um MÍSSIL em voo** (gate V28G.L). Uma lista própria, e não um projétil.
 *
 * ## Porque não é um campo em `pvpProjectileFrameSchema`
 *
 * Porque não é um projétil. Um míssil não substitui um tiro nem se pendura num:
 * ele nasce ao lado da salva, e a salva sai inteira. Marcá-lo com uma bandeira
 * na lista dos tiros faria a cena procurar um projétil que ela nunca vai
 * encontrar - o servidor não emite nenhum para ele.
 *
 * E a forma é outra: um míssil carrega a sua DIREÇÃO, que um tiro não carrega.
 *
 * ## Porque a direção viaja, quando a de um tiro não viaja
 *
 * Porque um tiro voa em linha reta e a cena pode medir o rumo entre duas
 * posições desenhadas. Um míssil CURVA, e o que ele desenha é um objeto com
 * nariz: medir o rumo entre dois quadros interpolados poria o bico ligeiramente
 * ao lado do sítio para onde ele vai mesmo. A autoridade sabe a direção exata, e
 * mandá-la custa dois inteiros.
 *
 * É apresentação e mais nada: nenhuma mecânica do cliente a lê.
 *
 * ## O que ele nunca carrega
 *
 * Dano, alvo, nível, joia, tempo restante ou raio. A cena desenha um míssil a
 * voar; quem decide o que ele atinge, quando explode e quanto tira é a
 * autoridade, que o anuncia quando acontecer.
 */
export const pvpMissileFrameSchema = z.object({
  i: z.number().int().min(0),
  x: z.number().int(),
  y: z.number().int(),
  /** A direção normalizada, em unidades do vetor da arena. */
  dx: z.number().int(),
  dy: z.number().int(),
  /** Quem o lançou. */
  s: seatSchema,
});

/**
 * What a seat is under the influence of, as TICKS REMAINING.
 *
 * Sent rather than inferred: a client that counted down its own timers would
 * disagree with the room the moment a frame was late, and would show an effect
 * that had already expired. Every field is the room's answer.
 */
export const pvpEffectsFrameSchema = z.strictObject({
  /** Ticks of Double Damage left. Zero means none. */
  dd: z.number().int().min(0),
  /** Ticks of Speed left. */
  sp: z.number().int().min(0),
  /*
   * `mi` (Mirage ticks) and `mo` (where the illusions were born) were here, and
   * left with the mechanic.
   *
   * This is a `strictObject`, so their absence is ENFORCED rather than merely
   * intended: a frame still carrying either is refused by the protocol itself.
   * A client cannot draw an illusion it was never sent, and a server cannot
   * send one that any client would accept.
   */
  /** Teleport charges held. A charge is not a timer and never decays. */
  tp: z.number().int().min(0),
});

/**
 * **Um proc CONFIRMADO, no fio. A forma é uma; o vocabulário é o de cada modo.**
 *
 * ## Porque um EVENTO e não um estado
 *
 * Porque um corte é um acontecimento instantâneo, e um frame de estado só sabe
 * dizer o que existe agora. O renderer do duelo já o diz por escrito: um
 * projétil ausente do snapshot mais recente "acertou, expirou ou a partida
 * acabou, e este renderer não os distingue - desenhar um rebentamento aqui
 * seria inventar um acerto que a sala nunca relatou". Isto é a sala a relatá-lo,
 * e é a única porta legítima: inferir o proc pelo dano, pela textura do tiro ou
 * pelo perfil equipado é exatamente o que as regras deste gate proíbem.
 *
 * ## Uma fábrica, e não um schema copiado
 *
 * O duelo endereça por ASSENTO (`a`, `b`) e o 3×3 por PARTICIPANTE (`a1`..`b3`),
 * porque cada protocolo estabeleceu o seu vocabulário antes deste gate existir.
 * Forçar um deles a mudar seria reescrever um frame que já descreve partidas
 * jogadas. Então a forma - os nomes dos campos e o que cada um significa - é
 * literalmente a mesma função, e só o tipo do identificador difere.
 *
 * ## O que ele NÃO carrega, e é a maior parte dele
 *
 * Magnitude, nível, cópias, identificador de inventário, de conta, de skin ou de
 * socket, e nada de arte - cor, textura, escala, duração. O dano já viaja como
 * vida a descer nos frames de estado; a arte é do cliente.
 *
 * `e` é o EFEITO (`cyclone_cut`), nunca a joia que o originou. Uma cena que
 * soubesse o nome de uma joia seria uma cena a decorar um perfil, que é o que
 * `jewel-presentation.test.ts` proíbe - e por isso o nome da joia não tem sequer
 * um campo onde caber.
 *
 * ## `i` é a chave de deduplicação
 *
 * O id do projétil: monotónico dentro da sala, único, já persistido no snapshot,
 * e um projétil produz no máximo um corte. Um cliente que receba o mesmo `i`
 * duas vezes - um frame fora da cadência, um replay, uma correção - desenha uma
 * vez.
 *
 * Deliberadamente NÃO um contador novo. Um contador teria de sobreviver a uma
 * hibernação da sala, e um que recomeçasse do zero faria um cliente descartar
 * eventos novos por os julgar antigos.
 */
/**
 * O maior tique que um frame pode declarar: o tecto de uma partida, mais um.
 *
 * Declarado AQUI e verificado contra `PVP_TIME_LIMIT_TICKS` numa prova do
 * domínio, porque a direção da dependência não permite o contrário: `shared` não
 * conhece `game-domain`, e conhecer seria uma volta. É a mesma disciplina que o
 * módulo de arte segue com os tiques dos dois modos - declarar, e falhar um
 * teste no dia em que divergir.
 *
 * O `+1` cobre o instante do fim: uma sala que termina exatamente no limite
 * ainda emite o evento desse tique.
 */
export const PVP_MAX_WIRE_TICK = 20 * 120 + 1;

/**
 * Quantas parcelas um stack de veneno pode ter pago: **seis**.
 *
 * Declarado AQUI e verificado contra `POISON_TICK_COUNT` numa prova do domínio,
 * pela mesma disciplina de {@link PVP_MAX_WIRE_TICK}: `shared` não conhece
 * `game-domain`, e conhecer seria uma volta na direção das dependências. É uma
 * declaração que falha um teste no dia em que divergir, e não uma cópia que
 * envelhece em silêncio.
 *
 * Não é um teto sobre QUANTOS stacks existem - esse não existe, e o contrato
 * proíbe-o. É o teto de um contador que conta até seis por construção.
 */
export const PVP_MAX_WIRE_POISON_TICKS = 6;

/**
 * **A versão do protocolo do DUELO 1×1** (gate V28G.I).
 *
 * ## Porque um número novo, e não um que já existisse
 *
 * Porque nenhum dos que existem descreve este wire, e foi auditado antes de ser
 * escrito:
 *
 * | candidato | o que descreve | porque não serve |
 * | --- | --- | --- |
 * | `PVP_RULES_VERSION` | as REGRAS de combate | viaja no `welcome` como número a comparar, não negoceia forma nenhuma |
 * | `PVP_TEAM_PROTOCOL_VERSION` | o wire do 3×3 | outro protocolo, outro vocabulário de endereçamento |
 * | `PVP_SNAPSHOT_V3_VERSION` | a instantânea PERSISTIDA da simulação | disco, não fio |
 * | `PVP_ROOM_STATE_VERSION` | o estado persistido da sala | disco, não fio |
 * | `PVP_APPEARANCE_VERSION` | o perfil de aparência | outro documento |
 * | `PVP_OUTCOME_VERSION` | o registo de resultado | outro documento |
 *
 * O duelo nasceu sem versão e sobreviveu sem ela porque a sua forma nunca tinha
 * mudado de maneira incompatível. Mudou agora: os `procs` passam a trazer o
 * tique autoritativo, e `pvpProcFrameOf` é `.strict()`.
 *
 * ## Começa em 1, e é uma QUEBRA LIMPA
 *
 * Um cliente anterior a este gate continua a recusar um proc com `t` - a versão
 * não o torna compatível, torna a incompatibilidade **declarada**. Isso é
 * aceitável aqui e só aqui, porque nada foi implantado: não há cliente remoto
 * antigo, não há sessão remota ativa, e nenhum formato persistido depende deste
 * frame - `PvpRoomPersistedState` guarda o estado da SALA, não os seus frames.
 *
 * Registado como quebra anterior ao primeiro deploy, e não como compatibilidade
 * retroativa. Depois do primeiro deploy, este número passa a ter o mesmo peso
 * que o do 3×3.
 *
 * ## A fronteira: o FRAME, como no 3×3
 *
 * A unidade negociada é o frame do servidor, e são estampados os três que a
 * partida possui - `welcome`, `state` e `ended`. `error` e `closing` ficam de
 * fora porque são PARTILHADOS com o 3×3: estampá-los obrigaria um dos dois
 * protocolos a carregar a versão do outro.
 *
 * ## 1 -> 2: o Núcleo Atômico (gate V28G.J)
 *
 * Três campos, e nenhum deles é dedutível do que já viajava:
 *
 * - um projétil pode dizer que é uma **bomba** (`b`), porque a cena desenha-a de
 *   outra maneira e não tem por onde adivinhar;
 * - um casco pode dizer quantos tiques de **empurrão** lhe faltam (`kb`), porque
 *   o preditor tem de deixar de prever enquanto durar;
 * - o frame de estado carrega as **explosões** confirmadas (`blasts`), com o
 *   centro, o raio mecânico e quem levou.
 *
 * `blasts` é obrigatório e os outros dois são opcionais, e a assimetria é a
 * mesma que `procs` já tinha: uma lista vazia diz "não houve nenhuma", e um
 * campo ausente num projétil diz "não é uma bomba" - as duas ausências são
 * exatas, e nenhuma delas significa "não sei".
 *
 * ## 2 -> 3: o Arsenal Ígneo (gate V28G.L)
 *
 * Um campo, e ele não é dedutível de nada que já viajava: o frame de estado
 * passa a carregar os **mísseis em voo** (`mis`), cada um com a sua posição e a
 * sua direção. Uma lista própria e obrigatória, ao lado de `proj`, porque um
 * míssil não é um projétil desta partida - ele nasce ao lado da salva sem
 * substituir tiro nenhum, e um cliente da versão 2 não desenharia coisa alguma.
 *
 * `blasts` não muda de forma: uma explosão de míssil viaja pelo mesmo canal com
 * outro `e` e com `r: 0`, porque o raio mecânico dela é mesmo zero.
 *
 * ## 3 -> 4: o Orbe da Víbora (gate V28G.M)
 *
 * Um campo, e ele não é dedutível de nada que já viajava: o frame de estado passa
 * a carregar os **stacks de veneno ativos** (`psn`), cada um com a sua
 * identidade, o casco que o carrega e quantas parcelas já pagou.
 *
 * É a primeira coisa neste protocolo que é **estado e não evento**, e a diferença
 * é a razão do campo. Um proc e uma explosão acontecem e acabam, portanto viajam
 * como listas do que houve desde o frame anterior; um stack DURA seis segundos e
 * tem uma marca desenhada durante esse tempo. Um cliente que chegasse a meio -
 * uma reconexão, um espetador - não veria marca nenhuma se ela viajasse por um
 * canal de eventos.
 *
 * Obrigatório, como `mis` e `blasts` são: uma lista vazia diz "não há nenhum" em
 * vez de "não sei".
 *
 * O dano de cada parcela **não** ganha campo nenhum. Ele já viaja como sempre
 * viajou - vida a descer no frame de estado - e o duelo nunca teve números de
 * dano flutuantes para começar.
 *
 * ## 4 -> 5: a Garra Hemorrágica (gate V28G.N)
 *
 * Três campos, e nenhum deles é dedutível do que já viajava:
 *
 * - o comando pode carregar a **ordem de detonar** (`bd`). É o primeiro campo do
 *   cliente para uma ação de combate que não é mover, mirar nem disparar, e não
 *   há forma de a inferir: um servidor que a adivinhasse pelo padrão dos frames
 *   estaria a decidir quando o jogador quis;
 * - o frame de estado carrega as **marcas ativas** (`bld`), cada uma com a sua
 *   identidade, quem a pôs e quem a carrega. Estado e não evento, como `psn`;
 * - o frame de estado carrega as **detonações confirmadas** (`bdt`). Evento, e
 *   necessário ao lado do estado precisamente porque as marcas saírem da lista é
 *   ambíguo: elas saem quando rebentam, quando o alvo morre e quando o autor
 *   morre, e uma cena que inferisse a detonação de "a lista encolheu" acenderia
 *   um corte carmesim sobre um casco que apenas foi abatido.
 *
 * `bld` e `bdt` são obrigatórios como `psn`, `mis` e `blasts` são: uma lista
 * vazia diz "não há nenhuma" em vez de "não sei". `bd` é opcional como `tp` é,
 * porque a maior parte dos frames não pede nada.
 *
 * ## 5 -> 6: o número vermelho da detonação (gate V28G.N)
 *
 * Um campo, e ele REVERTE uma decisão do degrau anterior: `bdt.q[].d` passa a
 * carregar o dano realmente aplicado a cada casco.
 *
 * O degrau 5 dizia que o dano não ganhava campo nenhum "porque ele já viaja como
 * vida a descer no frame de estado". Isso era verdade enquanto o duelo não
 * imprimisse números de dano - e deixou de ser quando o dono pediu um indicador
 * vermelho a sair da nave. A vida a descer diz que DOEU; não diz QUANTO, porque
 * no mesmo tique ela também desceu por tiros, e as duas parcelas são
 * indistinguíveis de fora.
 *
 * O cliente também não o pode calcular: ele não tem as magnitudes congeladas de
 * cada marca. Ou o número vem da autoridade, ou é inventado.
 *
 * ## 6 -> 7: a Égide Espinhosa (gate V28G.O)
 *
 * Um campo, e ele não é dedutível de nada que já viajava: o frame de estado
 * passa a carregar as **reflexões confirmadas** (`rfl`), cada uma com o
 * refletor, o destinatário, o tipo de payload intercetado, o estágio e as DUAS
 * posições que o brilho e o feixe precisam.
 *
 * Nada disto sai do estado. Uma barra a descer não diz que houve uma reflexão -
 * diz que alguém perdeu vida, e num tique em que os dois estão a atirar isso é
 * indistinguível de um tiro normal. E o cliente não pode calcular o número: ele
 * não tem as magnitudes congeladas.
 *
 * Obrigatória, como `blasts`, `mis`, `psn`, `bld` e `bdt` são: uma lista vazia
 * diz "não houve nenhuma" em vez de "não sei".
 */
/*
 * 8 -> 9: a vida passa a MILLI, e o quadro deixa de a truncar (V28G.S.1).
 *
 * Tres campos mudam de UNIDADE e de TIPO ao mesmo tempo: `hp` de cada casco,
 * `maxHealthMilli` da arena - que era `maxHealth` - e as magnitudes de vida que
 * viajam nos eventos (`h` de um proc, `d` de uma reflexao e de uma detonacao).
 * Os tres passam a strings decimais em milesimos.
 *
 * Nao e um campo novo: e o mesmo facto noutra unidade, e e por isso que a
 * versao TEM de subir. Um cliente da versao 8 leria `"108120"` onde espera
 * `108` e desenharia uma barra mil vezes maior, ou recusaria o quadro - e
 * nenhuma das duas e uma falha que se veja a tempo. O bump faz a recusa
 * acontecer no aperto de mao, com nome.
 *
 * **11 (gate V28H.L): as cartas de temporizador.**
 *
 * Duas coisas mudaram na forma, e as duas obrigam:
 *
 * - o casco ganhou `fz` e `bt` - congelado e inalvejável. Os dois são factos
 *   públicos porque mudam o que acontece a quem está do outro lado: uma nave
 *   que parou de responder, uns tiros que atravessam;
 * - a LISTA de cascos passou a poder ser mais curta do que a sala. Um casco com
 *   o Manto Espectral é **omitido** do frame de quem não é da equipa dele, e um
 *   cliente antigo leria essa ausência como uma nave que desapareceu do mapa em
 *   vez de uma que não pode ver.
 *
 * **12 (gate V28H.L): as invocações e o que as cartas desenham.**
 *
 * `sm` - a lista de invocações - e `cv` no casco, a lista do que as cartas dele
 * estão a desenhar. Um campo novo num schema estrito é uma forma nova, e um
 * cliente da versão anterior recusa-a, que é a razão de o número existir.
 *
 * **13 (gates V28H.N e V28H.O): a carga solta-se, e o círculo é passivo.**
 *
 * Três coisas, e todas mudam a FORMA de um objecto estrito:
 *
 * - `cr` no comando: a ordem de SOLTAR uma carga. A Lança Solar passou a
 *   carregar enquanto a tecla está premida, e o instante em que o dedo sai é
 *   uma decisão do jogador - uma sala que não conheça o campo recusaria o frame
 *   inteiro, e o jogador ficaria com uma carta que carrega e nunca dispara;
 * - `o` na lista de `cv`: o círculo mágico do Turbilhão é passivo e viaja pelo
 *   duelo inteiro. Uma ponta antiga recusa a letra que não conhece;
 * - `cg` e `cw` no casco: quanto da carga já foi cumprido, e com que força o
 *   feixe arde. São eles que fazem a antecipação e a espessura desenhadas serem
 *   as mesmas que a sala cobra.
 *
 * **14 (gate V28H.Q): o nível de uma carta, na metade privada.**
 *
 * `l` em cada espaço de `ck`. A barra mostra o nível da carta, e sem o número o
 * projector preenchia `1` - o dono viu "Nível 1" numa carta de nível 6. Ele viaja
 * só para o dono da cadeira, ao lado dos arrefecimentos dela; o id da instância
 * continua a não sair do servidor, porque esse é posse.
 *
 * Um campo novo num schema estrito é uma forma nova, e um cliente da versão
 * anterior recusa-a - que é a razão de o número existir.
 *
 * **15 (gate V28H.R): o NICKNAME de cada cadeira.**
 *
 * `nick` no welcome do duelo e `n` na entrada do roster do 3x3, ao lado da
 * aparência e pela mesma razão: os dois são estáticos dentro de uma partida.
 *
 * A cor dos efeitos de carta deixou de dizer de quem eles são - ela passou a ser
 * a escolha de quem olha, e vale para todos - e a etiqueta por cima do casco é
 * uma das duas coisas que passaram a dizê-lo, com a barra de vida.
 *
 * Um campo novo num schema estrito é uma forma nova, e um cliente da versão
 * anterior recusa-a - que é a razão de o número existir.
 *
 * **16 (gate V28H.U): a bomba de carta diz-se, e a Esfera carrega-se à vista.**
 *
 * `cb` e `br` no projétil, e a letra `g` em `cv`. As três são forma nova num
 * schema estrito, e as três existem porque o que faltava era VERDADE e não
 * enfeite: sem `cb` a bomba do Catolé - que tem seis vezes o raio da Bomba
 * Atômica - era desenhada como um tiro comum; sem `br` a esfera de uma carga de
 * um toque tinha o tamanho de uma carga cheia, e o adversário não podia julgar
 * o que vinha na direcção dele; sem `g` a cena sabia que ALGUMA carta carregava
 * e não qual, portanto desenhava a corona da Lança por cima de uma esfera.
 *
 * Um cliente da versão anterior recusa a forma nova, que é a razão do número.
 *
 * **17 (gate V28H.V): o que as três cartas de MOVIMENTO desenham.**
 *
 * As letras `d`, `h` e `k` em `cv` - o Arranque a avançar, a Sobremarcha ligada
 * e o Manto ligado. Sem elas a cena não tinha como saber que qualquer das três
 * estava a acontecer: um avanço lê-se como uma nave a andar depressa, mais
 * velocidade lê-se como nada, e um casco encoberto chegava ao dono exactamente
 * igual a um casco normal.
 *
 * Deduzi-las era possível e errado. O avanço podia sair de `kb`, que já viaja -
 * mas `kb` é também o empurrão de uma explosão, e as duas coisas não se
 * desenham da mesma maneira. As outras duas não têm sequer por onde ser
 * adivinhadas.
 *
 * Um cliente da versão anterior recusa a letra que não conhece.
 *
 * **22 (gate V28H.X): a Marca da Caveira, vista de quem a lança.**
 *
 * Uma letra em `cv` - `m` - porque a carta marcava o adversário sem nada
 * acontecer de quem a lançou, e um cliente não tem como saber que ela saiu: ela
 * não tem projéctil, não tem carga e não deixa nada no casco de quem a deu.
 *
 * **21 (gate V28H.X): a salva do Leviatã diz que é dele.**
 *
 * Uma letra num projéctil - `sv` - porque os seis canhões da estrutura disparam
 * vermelho néon, e a cor de um tiro sai hoje da cadeira de quem o deu. O piloto
 * está escondido lá dentro, portanto sem isto a salva sairia da cor de uma nave
 * que ninguém está a ver.
 *
 * **20 (gate V28H.X): a ordem de PILOTAR.**
 *
 * O embarque no Leviatã deixou de acontecer ao encostar e passou a ser pedido,
 * portanto há uma ordem nova - `bo` - e um cliente da versão anterior não a sabe
 * mandar: ele encostaria à estrutura e nada aconteceria.
 *
 * **19 (gate V28H.X): o raio, a marca e o curto-circuito.**
 *
 * Uma letra nova em `cb` - `s`, o raio do Curto-Circuito, que passou a disparar
 * em vez de tocar - e dois estados no casco: `cc` e `sk`.
 *
 * Os dois são invisíveis sem isto, e de maneiras diferentes: a cadência reduzida
 * só se nota ao fim de vários tiros, e o dano reduzido não se nota nunca. Uma
 * carta chamada "Marca da Caveira" que não desenha marca nenhuma é a descrição
 * dela a não ser verdade.
 *
 * **18 (gate V28H.W): o gelo, e o que ele deixa no casco.**
 *
 * Duas letras novas em `cb` - `z` para a esfera do Congelamento e `t` para a
 * estrela da Lentidão - e quatro campos no casco: `mw`, `ft`, `pz` e `sl`.
 *
 * Nenhum deles é deduzível. As duas cartas de gelo passaram a LANÇAR um corpo, e
 * um corpo sem letra é desenhado como um tiro comum; a casca de gelo tem três
 * espessuras e a cena não sabe qual; a paralisia total distingue-se da parcial
 * pela magnitude de uma ranhura que a cena nunca vê; e o Malware não tem
 * absolutamente nada no frame que o denuncie.
 */
export const PVP_DUEL_PROTOCOL_VERSION = 22;

/**
 * Uma EXPLOSÃO confirmada pela autoridade (gate V28G.J).
 *
 * ## Porque não é um `proc`
 *
 * Porque um proc tem um alvo e uma explosão tem um CENTRO e um RAIO. Uma bomba
 * que detona contra uma parede não atingiu ninguém, e o `q` obrigatório de um
 * proc não teria o que lá pôr - um id inventado seria uma mentira que a cena
 * desenharia sobre um casco que não levou nada.
 *
 * ## O raio viaja, e é o raio MECÂNICO
 *
 * O §5 exige que a onda de choque desenhada represente o raio real. Uma cena com
 * o seu próprio raio teria uma segunda cópia dele, e o dia em que uma das duas
 * mudasse seria o dia em que o círculo passava a mentir.
 */
function pvpBlastFrameOf<T extends z.ZodTypeAny>(id: T) {
  return z
    .object({
      /** O projétil que detonou. A chave de deduplicação num replay. */
      i: z.number().int().min(0),
      /** O EFEITO confirmado, nunca a joia. */
      e: z.string().min(1).max(32),
      /** Quem a lançou. */
      p: id,
      /** O tique da SIMULAÇÃO em que ela detonou. Nunca o de publicação. */
      t: z.number().int().min(0).max(PVP_MAX_WIRE_TICK),
      /** O centro, em unidades de mundo. */
      x: z.number().int(),
      y: z.number().int(),
      /** O raio MECÂNICO, o mesmo que decidiu quem levou. */
      r: z.number().int().min(0),
      /**
       * Quem levou dano, e só quem levou.
       *
       * Não é "quem estava por perto": um casco que já estava sem vida não entra.
       * A cena pisca exatamente estes, que é o que o §9 pede - "flash breve sobre
       * as naves realmente atingidas".
       */
      q: z.array(id),
    })
    .strict();
}

/** Uma explosão num duelo, endereçada por assento. */
export const pvpBlastFrameSchema = pvpBlastFrameOf(seatSchema);
export type PvpBlastFrame = z.infer<typeof pvpBlastFrameSchema>;

/**
 * **Um stack de veneno ATIVO, no fio** (gate V28G.M).
 *
 * ## Porque é ESTADO e não um evento, ao contrário do proc e da explosão
 *
 * Porque um corte e uma explosão são instantâneos - acontecem e acabam - e um
 * stack DURA. Ele existe durante seis segundos, tem uma marca desenhada sobre o
 * casco durante esse tempo, e um cliente que chegasse a meio (uma reconexão, um
 * espetador) tem de o ver: um canal de eventos só diria o que aconteceu desde o
 * frame anterior, e a marca só apareceria para quem estava a ver quando ela
 * nasceu.
 *
 * ## Três campos, e nem um a mais
 *
 * - `i` é a identidade. É o que faz a gota deste frame ser a MESMA gota do frame
 *   anterior em vez de uma nova - sem ela, uma cena não distinguiria "cinco
 *   stacks" de "cinco stacks diferentes cinco vezes por segundo";
 * - `q` é quem o carrega, para a cena saber sobre que casco desenhar;
 * - `n` é quantas parcelas já foram cobradas. É o que faz a marca PULSAR: a cena
 *   pulsa quando este número sobe, e não porque um evento lho disse. O pulso é
 *   decoração derivável do estado, e um protocolo a mais para ele seria um
 *   protocolo a mais para nada.
 *
 * ## O que ele deliberadamente NÃO carrega
 *
 * Quem o aplicou, a magnitude, o total, o instante da aplicação, o nível, a
 * joia, a cor, o tamanho e a duração do pulso. A autoria é MECÂNICA - decide o
 * crédito da morte no servidor - e nenhuma cena a lê; o dano já viaja como vida
 * a descer nos frames de estado; e a arte é do cliente.
 *
 * Uma cena que soubesse o nome de uma joia seria uma cena a decorar um perfil,
 * e por isso o nome da joia não tem sequer um campo onde caber.
 */
function pvpPoisonFrameOf<T extends z.ZodTypeAny>(id: T) {
  return z
    .object({
      /** A identidade do stack. Estável enquanto ele existir. */
      i: z.number().int().min(0),
      /** Em quem. Autoritativo: o cliente não escolhe um alvo. */
      q: id,
      /** Quantas das seis parcelas já foram cobradas. Sobe uma vez por segundo. */
      n: z.number().int().min(0).max(PVP_MAX_WIRE_POISON_TICKS),
    })
    .strict();
}

/** Um stack num duelo, endereçado por assento. */
export const pvpPoisonFrameSchema = pvpPoisonFrameOf(seatSchema);
export type PvpPoisonFrame = z.infer<typeof pvpPoisonFrameSchema>;

/**
 * **Uma marca de sangramento ATIVA, no fio** (gate V28G.N).
 *
 * Estado e não evento, pela mesma razão exata que o stack de veneno é: ela existe
 * até alguém a mandar rebentar, tem um corte desenhado durante esse tempo, e um
 * cliente que chegasse a meio - uma reconexão, um espetador - não veria marca
 * nenhuma se ela viajasse por um canal de eventos.
 *
 * ## Três campos, e nem um a mais
 *
 * - `i` é a identidade. É o que faz o corte deste frame ser o MESMO corte do
 *   frame anterior, e não um novo desenhado vinte vezes por segundo;
 * - `p` é quem a pôs. **Viaja, ao contrário do autor de um stack de veneno**, e a
 *   razão é mecânica antes de ser visual: é a autoria que decide quem pode
 *   detonar, portanto é ela que decide o que o botão de um jogador conta e que
 *   marcas o efeito de uma detonação anima. Uma cena que a adivinhasse contaria
 *   as marcas de um aliado. Não é segredo nenhum: os assentos de um duelo são
 *   públicos, e o dano já viaja como vida a descer;
 * - `q` é quem a carrega, para a cena saber sobre que casco desenhar.
 *
 * ## O que ela deliberadamente NÃO carrega
 *
 * A magnitude congelada, o instante da aplicação, o nível, a joia, a cor, o
 * tamanho e a duração do flash. O dano viaja como vida a descer nos frames de
 * estado; a arte é do cliente. Uma cena que soubesse o nome de uma joia seria uma
 * cena a decorar um perfil, e por isso o nome da joia não tem sequer um campo
 * onde caber.
 *
 * **E não carrega prazo nenhum**, porque não existe: uma marca não expira.
 */
function pvpBleedFrameOf<T extends z.ZodTypeAny>(id: T) {
  return z
    .object({
      /** A identidade da marca. Estável enquanto ela existir. */
      i: z.number().int().min(0),
      /** Quem a pôs, e o único que a pode detonar. */
      p: id,
      /** Em quem. Autoritativo: o cliente não escolhe um alvo. */
      q: id,
    })
    .strict();
}

/** Uma marca num duelo, endereçada por assento. */
export const pvpBleedFrameSchema = pvpBleedFrameOf(seatSchema);
export type PvpBleedFrame = z.infer<typeof pvpBleedFrameSchema>;

/**
 * **Uma REFLEXÃO confirmada, no fio** (gate V28G.O).
 *
 * ## Porque nenhum dos quatro canais que já existem serve
 *
 * Um `proc` deduplica pelo PROJÉTIL que o trouxe, e uma reflexão pode intercetar
 * uma parcela de veneno que nenhum projétil trouxe. Um `blast` tem um centro e
 * um raio, e uma reflexão não tem área. Uma detonação acontece em vários cascos
 * por uma ordem, e uma reflexão tem exatamente dois intervenientes. E o estado -
 * `psn`, `bld` - descreve coisas que DURAM, e uma reflexão acontece e acaba.
 *
 * Mas a razão decisiva é que uma reflexão precisa de **duas posições**: o brilho
 * percorre a silhueta do refletor e o feixe termina no atacante. Um evento com
 * um ponto só obrigaria a cena a ir buscar o outro ao estado do frame - que é o
 * estado do TIQUE DE PUBLICAÇÃO, e não o do tique em que a interceção aconteceu.
 * Num frame que junta dois tiques, os dois cascos já se moveram.
 *
 * ## Os dois estágios, e porque nenhum é derivável do outro
 *
 * - `i` — **intercepted**: a autoridade confirmou a interceção. Acende o brilho
 *   e, quando o payload era um tiro comum, desenha o feixe. Acontece mesmo
 *   quando nada chega a doer: um míssil refletido pode ir bater numa pilastra, e
 *   o jogador tem de ver que defendeu;
 * - `l` — **landed**: o payload redirecionado causou dano. Imprime `REFLECT`
 *   sobre o número. Para o Míssil e para a Bomba chega SEGUNDOS depois, que é
 *   exatamente o §7 do contrato.
 *
 * O cliente não pode derivar o segundo do primeiro: ele não tem as magnitudes
 * congeladas, e a barra move-se por tiros do mesmo tique. Nem o primeiro do
 * segundo: um payload refletido pode nunca aterrar.
 *
 * ## O que ela deliberadamente NÃO carrega
 *
 * A magnitude congelada, o nível, a joia, a cor, a espessura do feixe e a
 * duração do brilho. O `k` é o TIPO de payload intercetado - `cannon_shot`,
 * `missile_impact` - e nunca a peça que o emitiu: uma cena que soubesse o nome
 * de uma joia seria uma cena a decorar um perfil, e por isso o nome da joia não
 * tem sequer um campo onde caber.
 *
 * A chave de deduplicação é `(p, q, t, k, s)`.
 */
function pvpReflectFrameOf<T extends z.ZodTypeAny>(id: T) {
  return z
    .object({
      /** O EFEITO confirmado, nunca a joia: `reflection`. */
      e: z.string().min(1).max(32),
      /** O REFLETOR, e o novo autor do payload. O brilho é dele. */
      p: id,
      /** Quem recebe o payload redirecionado: o atacante original. */
      q: id,
      /** O tique da SIMULAÇÃO. Nunca o de publicação. */
      t: z.number().int().min(0).max(PVP_MAX_WIRE_TICK),
      /** Que instância hostil foi intercetada. Um tipo, nunca uma joia. */
      k: z.string().min(1).max(32),
      /** `i` acende o brilho; `l` imprime o número. */
      s: z.enum(['i', 'l']),
      /** O refletor, em MILLI-unidades: onde o brilho corre. */
      x: z.number().int(),
      y: z.number().int(),
      /** O destinatário: onde o feixe acaba, e onde o número é impresso. */
      tx: z.number().int(),
      ty: z.number().int(),
      /**
       * O dano REALMENTE aplicado, em MILLI (gate V28G.S.1).
       *
       * Presente só em `l`, e ausente em `i` - onde ainda não há dano nenhum a
       * declarar. Um zero ali seria "não doeu" em vez de "ainda não aconteceu".
       */
      d: pvpHealthMilliSchema.optional(),
    })
    .strict();
}

/** Uma reflexão num duelo, endereçada por assento. */
export const pvpReflectFrameSchema = pvpReflectFrameOf(seatSchema);
export type PvpReflectFrame = z.infer<typeof pvpReflectFrameSchema>;

/**
 * **Uma DETONAÇÃO confirmada, no fio** (gate V28G.N).
 *
 * ## Porque um evento, quando as marcas já são estado
 *
 * Porque as marcas desaparecerem é AMBÍGUO. Elas saem do estado quando são
 * detonadas, quando o alvo morre e quando o autor morre, e uma cena que inferisse
 * a detonação de "a lista encolheu" acenderia um corte carmesim sobre um casco
 * que apenas foi abatido. Isto é a sala a dizer qual das três aconteceu.
 *
 * ## Porque não é um `proc` nem um `blast`
 *
 * Um `proc` deduplica pelo PROJÉTIL que o trouxe, e uma detonação não tem
 * projétil - a marca sobreviveu ao tiro que a semeou por segundos ou minutos. Um
 * `blast` tem um centro e um raio, e uma detonação não tem nem um nem outro: ela
 * acontece em vários cascos ao mesmo tempo, cada um com a sua contagem.
 *
 * A chave de deduplicação é `(p, t)`: um autor detona no máximo uma vez por
 * tique, por construção, porque a ordem é consumida uma vez.
 *
 * ## Um por DETONAÇÃO, e não um por alvo
 *
 * Porque a ordem foi uma só. Os alvos vêm dentro, cada um com quantas marcas
 * DAQUELE autor rebentaram nele - que é o que deixa a intensidade do efeito
 * refletir a quantidade sem a cena ter de contar marcas que já saíram do estado.
 *
 * O dano **não** ganha campo nenhum: ele já viaja como vida a descer no frame de
 * estado, e o duelo nunca teve números de dano flutuantes.
 */
function pvpBleedBurstFrameOf<T extends z.ZodTypeAny>(id: T) {
  return z
    .object({
      /** Quem mandou. Metade da chave de deduplicação. */
      p: id,
      /** O tique da SIMULAÇÃO em que a ordem foi executada. A outra metade. */
      t: z.number().int().min(0).max(PVP_MAX_WIRE_TICK),
      /** Um alvo detonado, quantas marcas rebentaram nele, e o que doeu. */
      q: z.array(
        z
          .object({
            s: id,
            n: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
            /**
             * O dano REALMENTE aplicado a este casco, em pontos inteiros.
             *
             * ## Porque ele viaja, quando o do veneno não viajava
             *
             * Porque o duelo passou a IMPRIMIR um número por detonação, e o
             * cliente não o pode recalcular: ele não tem as magnitudes
             * congeladas de cada marca, e a diferença observada na barra move-se
             * também pelos tiros do mesmo tique. O único que sabe quanto doeu é
             * quem o aplicou.
             *
             * Já DEPOIS do clamp do overkill, e nunca a soma bruta das marcas:
             * um casco com três de vida que leve dez marcas perde três.
             *
             * Zero é possível e é honesto - um alvo que já estava sem vida - e
             * a cena não imprime número nenhum nesse caso.
             */
            d: pvpHealthMilliSchema,
          })
          .strict(),
      ),
    })
    .strict();
}

/** Uma detonação num duelo, endereçada por assento. */
export const pvpBleedBurstFrameSchema = pvpBleedBurstFrameOf(seatSchema);
export type PvpBleedBurstFrame = z.infer<typeof pvpBleedBurstFrameSchema>;

function pvpProcFrameOf<T extends z.ZodTypeAny>(id: T) {
  return z
    .object({
      /** O projétil que o trouxe. Metade da chave de deduplicação. */
      i: z.number().int().min(0),
      /** O efeito confirmado. Nunca a joia. */
      e: z.string().min(1).max(32),
      /** Quem cortou. */
      p: id,
      /** Em quem. Autoritativo: o cliente não escolhe um alvo. */
      q: id,
      /**
       * **O tique da SIMULAÇÃO em que o evento nasceu** (gate V28G.I).
       *
       * ## Nunca o tique de PUBLICAÇÃO
       *
       * O `k` do frame diz quando ele foi publicado, e um frame carrega mais do
       * que um tique - a sala simula a 20 Hz e publica à cadência de envio do
       * cliente. São dois números diferentes, e trocá-los faria dois factos de
       * tiques distintos parecerem simultâneos.
       *
       * ## Porque é obrigatório
       *
       * Porque o formato é negociado. O `procs` do duelo era opcional enquanto o
       * frame não tinha versão - a ausência tinha de significar "servidor
       * antigo". Agora há {@link PVP_DUEL_PROTOCOL_VERSION}, portanto a forma é
       * declarada e um campo em falta é um documento malformado, não um
       * servidor antigo.
       *
       * ## O limite
       *
       * {@link PVP_MAX_WIRE_TICK}: o tecto de uma partida mais um tique, porque
       * um tique acima dele é um tique que nenhuma sala pode ter simulado.
       *
       * ## O que ele NÃO faz
       *
       * Apresentação, e só: nenhuma mecânica o lê. O Ciclone também o carrega, e
       * isso não muda coisa nenhuma no corte - é a mesma informação sobre quando,
       * para o mesmo tipo de decisão de desenho.
       */
      t: z.number().int().min(0).max(PVP_MAX_WIRE_TICK),
      /**
       * **A vida REALMENTE acrescentada a `p`, em milli** (gate V28G.I).
       *
       * ## Porque o valor viaja
       *
       * Porque o cliente não pode recalculá-lo. Ele não tem o perfil, o nível
       * nem a percentagem - e não pode tê-los, porque uma cena que lesse joias
       * seria uma cena a decorar um perfil. A diferença observada na barra
       * também não serve: ela move-se por dano e por cura ao mesmo tempo, e de
       * fora as duas são indistinguíveis.
       *
       * ## Porque em MILLI e INTEIRO
       *
       * Porque a apresentação soma todas as curas do mesmo atacante no mesmo
       * tique antes de mostrar um número, e somar frações acumularia erro. Em
       * milli a soma é exata e a divisão acontece uma vez, ao formatar.
       *
       * A vida de um casco é inteira por formato, portanto num duelo isto é
       * sempre um múltiplo de mil. Fica em milli na mesma para os dois modos
       * somarem na mesma unidade.
       *
       * ## Opcional, porque nem todo o efeito cura
       *
       * O corte do Ciclone não tem magnitude de cura, e um zero seria "curou
       * nada" em vez de "não é uma cura". A ausência é a distinção.
       */
      h: pvpHealthMilliSchema.optional(),
      /** Onde o efeito foi confirmado, em unidades de mundo. */
      x: z.number().int(),
      y: z.number().int(),
    })
    .strict();
}

/** Um proc num duelo, endereçado por assento. */
export const pvpProcFrameSchema = pvpProcFrameOf(seatSchema);
export type PvpProcFrame = z.infer<typeof pvpProcFrameSchema>;

/** A pad, and what is standing on it. Position comes from the shared balance. */
export const pvpRuneFrameSchema = z
  .object({
    /** Index into the arena's pad list. */
    p: z.number().int().min(0),
    /** What is on it, or null while it regrows. */
    k: z.enum(PVP_RUNE_WIRE_KINDS).nullable(),
  })
  /*
   * Strict, like every other authoritative shape in this file.
   *
   * A pad carries an index and a kind. A pad that also carried coordinates
   * would be a second source of truth for where a rune is, and the two could
   * disagree - which is exactly what naming it by index avoids. This was
   * non-strict when it was written and `rune-security.test.ts` caught it.
   */
  .strict();

export const pvpWelcomeSchema = z.object({
  t: z.literal('welcome'),
  /** {@link PVP_DUEL_PROTOCOL_VERSION}. A forma é negociada, não adivinhada. */
  v: z.literal(PVP_DUEL_PROTOCOL_VERSION),
  matchId: z.string(),
  /** Which seat YOU are. Never a list the client can choose from. */
  seat: seatSchema,
  state: z.enum(PVP_MATCH_STATES),
  mode: z.string(),
  rulesVersion: z.number().int(),
  /**
   * The highest command sequence the room has already applied for THIS seat.
   *
   * Zero on a first connection, and the reason this field exists is the second
   * one. The applied sequence belongs to the SHIP and survives a reconnection,
   * so a reloaded page that started counting at one again would have every
   * command it ever sent dropped as a replay - silently, and only while
   * reconnecting, which is the hardest kind of bug to see. The room says where
   * to resume instead of leaving the client to infer it from the first `ack`
   * that happens to arrive.
   */
  ack: z.number().int().min(0),
  tickMs: z.number().int(),
  /** The arena, so the client draws exactly the bounds the room enforces. */
  arena: z.object({
    w: z.number().int(),
    h: z.number().int(),
    obstacles: z.array(
      z.object({
        x: z.number().int(),
        y: z.number().int(),
        w: z.number().int(),
        h: z.number().int(),
      }),
    ),
  }),
  /**
   * What each seat looks like, decided by the server.
   *
   * BOTH seats, in one field, because a client draws its opponent as well as
   * itself and may not guess either. A seat that has not connected yet is
   * `null`; the room re-sends this same welcome to the seat that was already
   * present when the second one arrives, so the profile reaches both without a
   * new frame type. See `pvp/appearance.ts` for why it is authoritative.
   */
  appearance: z.object({
    a: pvpAppearanceSchema.nullable(),
    b: pvpAppearanceSchema.nullable(),
  }),
  /**
   * **O nome que aparece por cima de um casco** (gate V28H.R).
   *
   * ## Porque ele existe agora
   *
   * A cor dos efeitos de carta deixou de dizer de quem eles são - todas usam a
   * escolha de quem olha - e o dono nomeou as duas coisas que passam a dizê-lo: a
   * barra de vida, verde do meu lado e vermelha do outro, e **o nickname**.
   *
   * ## Porque no `welcome` e não no quadro
   *
   * Porque é estático: o nome de quem se sentou não muda dentro de uma partida. A
   * aparência viaja aqui pela mesma razão, e mandá-lo sessenta vezes por segundo
   * seria pagar por uma coisa que nunca muda.
   *
   * ## O que ele NÃO é
   *
   * Não é identidade nem posse. É o nome público que a conta já mostra em todo o
   * lado, e ele chega ao cliente pelo BILHETE assinado - nunca pelo socket -
   * portanto ninguém pode dizer que se chama outra coisa.
   *
   * Aparado a {@link PVP_NICKNAME_MAX} para uma etiqueta caber por cima de uma
   * nave, e para um nome longo não poder ser usado para tapar a arena.
   */
  nick: z.object({
    a: pvpNicknameSchema.nullable(),
    b: pvpNicknameSchema.nullable(),
  }),
  /**
   * What each hull IS, decided by the room (protocol version 3).
   *
   * BOTH seats, like the appearance beside it and for the same reason: a client
   * draws its opponent's health bar as well as its own, and predicting its own
   * movement needs its own `speedBps`. Reading either from a local constant is
   * how a browser comes to replay movement at a speed the room is not using.
   *
   * **Not nullable, unlike the appearance.** An appearance is null until its
   * holder presents a capability, because it describes a PERSON's loadout. A
   * profile describes a HULL, and both hulls exist from the moment the room
   * builds its simulation - so there is never an instant where the room does not
   * know one, and a nullable field would invite a client-side default.
   *
   * Nothing a client sends can change it: no command field, no route and no
   * client frame mentions a profile.
   */
  combatProfile: z.object({
    a: pvpCombatProfileSchema,
    b: pvpCombatProfileSchema,
  }),
  shipRadius: z.number().int(),
  projectileRadius: z.number().int(),
  /**
   * A referencia NORMALIZADA por que a metrica do casco divide, em MILLI.
   *
   * `PVP_MAX_HEALTH_MILLI`, e nao um teto: e o `1x` da curva, e e por ele que
   * `pvpVisualScale` divide para desenhar um casco maior. O denominador de uma
   * BARRA e outra coisa - `combatProfile[participante].maxHealthMilli` - e a
   * diferenca entre as duas perguntas e a razao de este campo continuar aqui.
   *
   * Um `number` e nao uma string: e uma constante do modo, nao a vida de
   * ninguem, e um milhao cabe num inteiro seguro com folga de sobra.
   */
  maxHealthMilli: z.number().int(),
  /** Milliseconds a duel may last before it is a draw. */
  timeLimitMs: z.number().int(),
  /** How long a dropped seat has to come back. */
  reconnectGraceMs: z.number().int(),
});

export const pvpStateFrameSchema = z.object({
  t: z.literal('state'),
  /** {@link PVP_DUEL_PROTOCOL_VERSION}. A forma é negociada, não adivinhada. */
  v: z.literal(PVP_DUEL_PROTOCOL_VERSION),
  /**
   * O tique de PUBLICAÇÃO. Monotónico; o cliente nunca o avança sozinho.
   *
   * Diz quando este frame saiu, e **não** quando cada facto dentro dele
   * aconteceu: um frame carrega mais do que um tique, e é por isso que cada
   * `proc` traz o seu próprio `t`.
   */
  k: z.number().int().min(0),
  state: z.enum(PVP_MATCH_STATES),
  /**
   * The highest command sequence the room has APPLIED for this connection.
   *
   * This is what makes local prediction reconcilable: the client replays its
   * own inputs after this number onto the position the room just sent, and
   * throws away everything at or below it.
   */
  ack: z.number().int().min(0),
  /**
   * Os TRÊS espaços de cartas desta ligação (gate V28H.J).
   *
   * Privado, e por isso está no frame e não na lista de naves: um
   * arrefecimento é informação de decisão, e este frame é construído por
   * ligação - o `ack` acima já o era. Que os canhões estão montados é público
   * e viaja em `ships[].sc`, porque os dois lados têm de os desenhar.
   */
  ck: pvpCardSlotsSchema,
  ships: z.array(pvpShipFrameSchema),
  proj: z.array(pvpProjectileFrameSchema),
  /**
   * Os mísseis em voo (gate V28G.L).
   *
   * Uma lista própria ao lado de `proj`, e obrigatória: este frame tem versão,
   * portanto uma lista vazia diz "não há nenhum" em vez de "não sei".
   */
  mis: z.array(pvpMissileFrameSchema),
  /**
   * As invocações a existir (gate V28H.L). Ausente = nenhuma.
   *
   * Uma lista à parte dos projéteis e dos mísseis, como eles são um do outro:
   * uma invocação não é um tiro, tem vida, e a cena desenha-a com uma barra.
   */
  sm: z.array(pvpSummonFrameSchema).optional(),
  /** Per-seat effects, in seat order, exactly as long as `ships`. */
  fx: z.array(z.object({ s: seatSchema, e: pvpEffectsFrameSchema })),
  /** Every pad and what is on it. */
  runes: z.array(pvpRuneFrameSchema),
  /**
   * Os procs confirmados DESDE O FRAME ANTERIOR, por ordem.
   *
   * Uma lista e não um campo: a sala simula a 20 Hz e publica à cadência de
   * envio do cliente, portanto um frame carrega MAIS DE UM TIQUE - e dois
   * factos podem acontecer entre duas publicações. Um campo singular perderia o
   * segundo em silêncio, e é por isso que cada elemento traz o seu `t`.
   *
   * **OBRIGATÓRIO desde o gate V28G.I**, e a razão é a versão. Enquanto o frame
   * do duelo não tinha uma, a ausência tinha de significar "servidor antigo" -
   * era a única leitura possível. Agora {@link PVP_DUEL_PROTOCOL_VERSION}
   * declara a forma, portanto uma lista em falta é um documento malformado e uma
   * lista vazia diz "não houve nenhum" em vez de "não sei". É a mesma decisão
   * que o 3×3 já tinha tomado, pela mesma razão.
   */
  procs: z.array(pvpProcFrameSchema),
  /**
   * As explosões confirmadas DESDE O FRAME ANTERIOR, por ordem (gate V28G.J).
   *
   * Uma lista e não um campo, pela mesma razão que `procs` é uma: um frame
   * carrega mais do que um tique, e duas bombas podem detonar entre duas
   * publicações. Cada elemento traz o seu próprio `t`.
   *
   * Obrigatória, e uma lista vazia diz "não houve nenhuma".
   */
  blasts: z.array(pvpBlastFrameSchema),
  /**
   * Os stacks de veneno ATIVOS agora, e não os que nasceram desde o frame
   * anterior (gate V28G.M).
   *
   * A diferença entre esta lista e `procs`/`blasts` é a diferença entre estado e
   * evento, e está escrita em {@link pvpPoisonFrameSchema}: um stack dura seis
   * segundos, e uma cena que só soubesse dos que nasceram enquanto ela estava
   * ligada não desenharia nada depois de uma reconexão.
   *
   * Obrigatória: uma lista vazia diz "não há nenhum".
   */
  psn: z.array(pvpPoisonFrameSchema),
  /**
   * As marcas de sangramento ATIVAS agora (gate V28G.N).
   *
   * Estado, como `psn`, e pela mesma razão: uma marca existe até alguém a mandar
   * rebentar, e uma cena que só soubesse das que nasceram enquanto ela estava
   * ligada não desenharia nada depois de uma reconexão.
   *
   * Obrigatória: uma lista vazia diz "não há nenhuma".
   */
  bld: z.array(pvpBleedFrameSchema),
  /**
   * As DETONAÇÕES confirmadas desde o frame anterior, por ordem (gate V28G.N).
   *
   * Evento, e ao lado do estado acima - a única lista deste protocolo que tem as
   * duas metades, e por uma razão concreta: as marcas saírem do estado é
   * ambíguo, e isto diz qual das três saídas aconteceu. Ver
   * {@link pvpBleedBurstFrameSchema}.
   *
   * Obrigatória, e uma lista vazia diz "não houve nenhuma".
   */
  bdt: z.array(pvpBleedBurstFrameSchema),
  /**
   * As REFLEXÕES confirmadas desde o frame anterior, por ordem (gate V28G.O).
   *
   * Evento, e não estado: uma reflexão acontece e acaba. Uma lista e não um
   * campo, pela mesma razão que `procs` é uma - um frame carrega mais do que um
   * tique, e duas interceções podem acontecer entre duas publicações.
   *
   * Obrigatória, e uma lista vazia diz "não houve nenhuma".
   */
  rfl: z.array(pvpReflectFrameSchema),
  /** Milliseconds left before the time limit. Presentation; never a reward. */
  left: z.number().int().min(0),
});

/**
 * Which seat abandoned, and whether they chose to.
 *
 * `.strict()`, so nothing rides along beside the two fields: no account, no
 * e-mail, no session, no ticket, and above all no SENTENCE. The server states a
 * fact and the client writes the words - a server that shipped prose would be a
 * server deciding what a screen says, in one language, forever.
 */
export const pvpAbandonmentSchema = z
  .object({
    cause: z.enum(PVP_ABANDON_CAUSES),
    seat: seatSchema,
  })
  .strict();

export const pvpEndedSchema = z.object({
  t: z.literal('ended'),
  /** {@link PVP_DUEL_PROTOCOL_VERSION}. A forma é negociada, não adivinhada. */
  v: z.literal(PVP_DUEL_PROTOCOL_VERSION),
  state: z.enum(PVP_MATCH_STATES),
  /** Absent winner means a draw OR no trustworthy result. `reason` says which. */
  winner: seatSchema.nullable(),
  reason: z.enum(PVP_END_REASONS),
  ticks: z.number().int().min(0),
  /**
   * Why an abandonment happened, `null` for every other ending - and ABSENT
   * from a frame written before this existed.
   *
   * Optional on purpose, and the one contract here that is not versioned: the
   * duel's frames carry no version field and never have, and this schema is not
   * strict, so an older client already drops what it does not recognise. Adding
   * a required key would refuse every duel in flight at deploy for a field that
   * only changes a sentence; adding an optional one costs a client that has to
   * mean something honest by its absence, which it does - it cannot explain that
   * abandonment, and it says so.
   */
  abandon: pvpAbandonmentSchema.nullable().optional(),
});

export const pvpErrorSchema = z.object({
  t: z.literal('error'),
  /** A stable machine reason. Never an exception, never an id, never a clock. */
  reason: z.string(),
});

export const pvpClosingSchema = z.object({
  t: z.literal('closing'),
  reason: z.enum(PVP_CLOSE_REASONS),
});

export const pvpServerFrameSchema = z.discriminatedUnion('t', [
  pvpWelcomeSchema,
  pvpStateFrameSchema,
  pvpEndedSchema,
  pvpErrorSchema,
  pvpClosingSchema,
]);

export type PvpWelcomeFrame = z.infer<typeof pvpWelcomeSchema>;
export type PvpStateFrame = z.infer<typeof pvpStateFrameSchema>;
export type PvpEndedFrame = z.infer<typeof pvpEndedSchema>;
export type PvpAbandonmentFrame = z.infer<typeof pvpAbandonmentSchema>;
export type PvpErrorFrame = z.infer<typeof pvpErrorSchema>;
export type PvpClosingFrame = z.infer<typeof pvpClosingSchema>;
export type PvpServerFrame = z.infer<typeof pvpServerFrameSchema>;
export type PvpShipFrame = z.infer<typeof pvpShipFrameSchema>;
export type PvpProjectileFrame = z.infer<typeof pvpProjectileFrameSchema>;
export type PvpMissileFrame = z.infer<typeof pvpMissileFrameSchema>;

/* -------------------------------------------------------------------------- */
/*  HTTP surface                                                              */
/* -------------------------------------------------------------------------- */

export const PVP_CHALLENGE_ROUTE = '/api/pvp/challenge';
export const PVP_ACCEPT_ROUTE = '/api/pvp/accept';
/**
 * Mints the short-lived authorisation the socket handshake carries.
 *
 * An ordinary authenticated POST, because a WebSocket upgrade is NOT one: it
 * carries cookies by `SameSite` rather than by CORS, and this API and its
 * client are on different sites in every environment that exists. See
 * `apps/api/src/pvp/ticket.ts`.
 */
export const PVP_TICKET_ROUTE = '/api/pvp/socket-ticket';
export const PVP_SOCKET_ROUTE = '/api/pvp/socket';

/**
 * Where a client asks whether the account it is signed in as is in a duel.
 *
 * ## Why this exists
 *
 * A reload loses everything the tab knew, including the match id that every
 * other route needs. Measured before it existed: the socket closed, the room
 * started its twelve-second reconnect grace exactly as designed, the page came
 * back, made no PvP request at all, and the grace expired into an abandonment
 * the opponent was told about. The room was right the whole time; the client
 * simply forgot.
 *
 * It is a READ, and it invents nothing. The answer is derived from the rows
 * that already decide this - an active participant row joined to a match in a
 * live state - which is the same pair `assertNotAlreadyPlaying` refuses a new
 * challenge on. There is no second source of state, in the browser or here.
 *
 * It is also scoped by the SESSION and by nothing else: there is no account
 * field to send, so asking about somebody else has no representation.
 */
export const PVP_ACTIVE_ROUTE = '/api/pvp/active';

/**
 * Giving up a duel that never launched, from outside the room (gate V27A).
 *
 * ## Why this route has to exist
 *
 * Leaving a duel is an intent sent over the socket, from INSIDE the room. That
 * is right for a fight - a match in progress is decided by the room and by
 * nothing else, and an HTTP call that could end one would be a way to escape a
 * duel you are losing.
 *
 * It is wrong for a room that never started. Measured on a real machine: a
 * player created a duel, the second window could not get in, and from then on
 * every attempt answered `already_in_match` while the panel offered only
 * `RETORNAR AO JOGO`. There was no way back into the room - it had died - and
 * therefore no way to send the intent that would have freed the seat. The
 * account was barred until somebody edited the database.
 *
 * So the split is by STATE, and it is the honest one: before the first tick the
 * seat is a reservation and its owner may drop it; after the first tick the
 * room owns the outcome. The team lobby already worked this way and has always
 * had `PVP_TEAM_LEAVE_ROUTE`; a duel simply never got the same door.
 *
 * Scoped by the session, like every other route here: there is no match id and
 * no account field to send, so cancelling somebody else's duel has no
 * representation.
 */
export const PVP_LEAVE_ROUTE = '/api/pvp/leave';

/** Strict and empty: the server decides which duel, because it seated you. */
export const pvpLeaveRequestSchema = z.object({}).strict();

/**
 * What was given up, if anything.
 *
 * `cancelled: false` is the honest answer to "I was not in one", and it is a
 * success rather than an error: a client that asks twice, or that asks after a
 * sweep already retired the room, has got what it wanted either way.
 */
export const pvpLeaveResponseSchema = z.object({ cancelled: z.boolean() }).strict();

export type PvpLeaveResponse = z.infer<typeof pvpLeaveResponseSchema>;

/**
 * What that answer looks like: a duel to rejoin, or nothing.
 *
 * Deliberately thin. It carries what a client needs to ask for a ticket and
 * to decide what to draw while it waits - never health, never a position,
 * never a result. Those come from the room, over the socket, after the
 * handshake that this only helps to start.
 */
export const pvpActiveResponseSchema = z.object({
  match: z
    .object({
      matchId: z.string().min(1),
      seat: seatSchema,
      mode: z.enum(PVP_MODES),
      state: z.string().min(1),
    })
    .nullable(),
});

export type PvpActiveResponse = z.infer<typeof pvpActiveResponseSchema>;

export const pvpTicketRequestSchema = z.object({ matchId: z.string().min(1) }).strict();

export const pvpTicketResponseSchema = z.object({
  ticket: z.string().min(1),
  seat: seatSchema,
  expiresAt: z.string(),
});

export type PvpTicketResponse = z.infer<typeof pvpTicketResponseSchema>;

/**
 * The invite code.
 *
 * Six characters from an alphabet with no `0`/`O` and no `1`/`I`/`L`, because a
 * code is read aloud and typed by somebody who did not choose it. 32^6 is about
 * a billion, which is not a security boundary and is not asked to be: the code
 * only names a room, and the SESSION decides who may sit in it.
 */
export const PVP_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const PVP_CODE_LENGTH = 6;

export const pvpCodeSchema = z
  .string()
  .length(PVP_CODE_LENGTH)
  .regex(new RegExp(`^[${PVP_CODE_ALPHABET}]+$`), 'invalid pvp code');

export const pvpChallengeRequestSchema = z
  .object({
    /**
     * Required, and validated against the playable list by the service.
     *
     * No default. A default would mean a request that forgot to say what it
     * wanted got a mode chosen for it, and one of the two modes moves Gold.
     */
    mode: z.string(),
  })
  .strict();

export const pvpAcceptRequestSchema = z.object({ code: pvpCodeSchema }).strict();

export const pvpChallengeResponseSchema = z.object({
  matchId: z.string(),
  code: z.string(),
  mode: z.string(),
  state: z.enum(PVP_MATCH_STATES),
  seat: seatSchema,
  expiresAt: z.string(),
});

export const pvpAcceptResponseSchema = z.object({
  matchId: z.string(),
  mode: z.string(),
  state: z.enum(PVP_MATCH_STATES),
  seat: seatSchema,
});

export type PvpChallengeResponse = z.infer<typeof pvpChallengeResponseSchema>;
export type PvpAcceptResponse = z.infer<typeof pvpAcceptResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  The private team lobby                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Where a private `team_3v3` roster is assembled, and only assembled.
 *
 * ## What these four routes are, and what they are not
 *
 * They fill six chairs in the database. They mint no capability, open no socket
 * and reach no Durable Object - a lobby can be COMPLETE and there is still no
 * way into combat, because the ticket that would carry six participants does
 * not exist. `/api/pvp/socket-ticket` refuses a team match by name rather than
 * signing a seat it does not have.
 *
 * ## Why a lobby needs its own read
 *
 * `PVP_ACTIVE_ROUTE` answers with a SEAT, and a team participant has none. So a
 * team lobby is read here instead, by an account that is in it - and a code
 * alone grants no read at all: holding one lets you take a chair, and taking a
 * chair is what lets you see the roster.
 */
export const PVP_TEAM_LOBBY_ROUTE = '/api/pvp/team/lobby';
export const PVP_TEAM_JOIN_ROUTE = '/api/pvp/team/join';
export const PVP_TEAM_LEAVE_ROUTE = '/api/pvp/team/leave';

/**
 * A team invite code: ten characters of the duel's alphabet.
 *
 * Longer than a duel's six on purpose. A duel's code is dictated aloud and its
 * uniqueness is only enforced among matches somebody could still join, because
 * a global unique on six characters would eventually refuse to open a
 * challenge. A team code is pasted, so it can afford ten - about 8.2e14
 * combinations - which makes a GLOBAL unique affordable, and a global unique is
 * a far simpler rule to reason about than a partial one whose predicate depends
 * on another table's state.
 *
 * The code carries no meaning: not the team, not the slot, not the account and
 * not the match id. Which group it opens is a column beside it on the server.
 */
export const PVP_TEAM_CODE_LENGTH = 10;

export const pvpTeamCodeSchema = z
  .string()
  .length(PVP_TEAM_CODE_LENGTH)
  .regex(new RegExp(`^[${PVP_CODE_ALPHABET}]+$`), 'invalid pvp team code');

/**
 * Strict and empty, both of them.
 *
 * There is nothing for a client to say. The team, the slot, the participant id,
 * the ruleset, the mode and the roster are all decided by the server, so a
 * request carrying any of them is refused by the schema rather than ignored by
 * the handler - a field that is quietly dropped is a field somebody will
 * eventually believe in.
 */
export const pvpTeamLobbyCreateRequestSchema = z.object({}).strict();
export const pvpTeamLeaveRequestSchema = z.object({}).strict();
export const pvpTeamJoinRequestSchema = z.object({ code: pvpTeamCodeSchema }).strict();

/**
 * One chair, as everybody in the lobby may see it.
 *
 * Occupied or not, and who is NOT part of the answer. An account id belongs to
 * the lobby, the ticket and the audit trail; publishing one would hand every
 * player in the room a stable identifier for five strangers.
 */
export const pvpTeamChairSchema = z.object({
  participantId: z.string().min(1),
  team: z.enum(['a', 'b']),
  slot: z.number().int().min(1).max(3),
  occupied: z.boolean(),
});

export const pvpTeamLobbySchema = z.object({
  matchId: z.string().min(1),
  ruleset: z.literal('team_3v3'),
  mode: z.enum(PVP_MODES),
  state: z.enum(PVP_MATCH_STATES),
  /**
   * Whether all six chairs are taken.
   *
   * DERIVED from the roster, and deliberately not called `ready`. Readiness is
   * the click each player makes inside the room once the sockets exist, and it
   * belongs to `PvpRoom`; this only says the lobby has stopped waiting for
   * people. A second thing called ready would be a second thing to disagree.
   */
  full: z.boolean(),
  /**
   * Whether the roster has stopped changing.
   *
   * Derived from the launch the server minted when it froze. The launch is not
   * a field of any lobby answer - it reaches a client only inside that client's
   * own signed capability, where it can be read but not altered. After this is
   * true nobody joins, leaves, swaps side or is replaced, which is what a
   * ticket needs to be safe for thirty seconds.
   */
  frozen: z.boolean(),
  expiresAt: z.string(),
  /** The caller's own chair. Every response is scoped to somebody who has one. */
  you: pvpTeamChairSchema,
  chairs: z.array(pvpTeamChairSchema),
  /**
   * The two invite codes, for the account that opened the lobby and nobody else.
   *
   * `null` for everybody else, including the five people already in the room:
   * whoever created it decides who else is invited, and a code handed to five
   * strangers is a code the creator can no longer account for.
   */
  codes: z.object({ ally: pvpTeamCodeSchema, opponent: pvpTeamCodeSchema }).nullable(),
});

export type PvpTeamChair = z.infer<typeof pvpTeamChairSchema>;
export type PvpTeamLobby = z.infer<typeof pvpTeamLobbySchema>;

/**
 * Where a frozen team roster becomes six signed capabilities, and one socket.
 *
 * ## Two routes, and why the socket is not the duel's
 *
 * A duel's capability names a SEAT and a team participant has none, so the two
 * formats are decoded by two modules whose signing keys are derived from
 * different domain labels - a version 4 ticket cannot be verified as a version
 * 5 one, and the reverse is equally impossible. Giving them one path would mean
 * one handler choosing a decoder, and the dangerous choice is the quiet one.
 *
 * ## The freeze comes first
 *
 * `POST /api/pvp/team/launch` stops the roster changing and mints the launch
 * every capability carries. Until it has run, `POST /api/pvp/team/socket-ticket`
 * refuses by name: a ticket minted while a chair could still be given up would
 * admit somebody who has left, into a chair that now belongs to another account.
 */
export const PVP_TEAM_LAUNCH_ROUTE = '/api/pvp/team/launch';
export const PVP_TEAM_TICKET_ROUTE = '/api/pvp/team/socket-ticket';
export const PVP_TEAM_SOCKET_ROUTE = '/api/pvp/team/socket';

/** Strict and empty: the server decides which chair the session already holds. */
export const pvpTeamLaunchRequestSchema = z.object({}).strict();
export const pvpTeamTicketRequestSchema = z.object({}).strict();

/**
 * What the issuing route answers.
 *
 * The ticket, and the chair it was minted for so a client can label its own
 * screen. No account id field, no launch id field and no code.
 *
 * The launch and the account ARE inside the capability, which is a signed
 * payload in the clear rather than an encrypted one - its holder can read its
 * own claims, and altering any of them invalidates the HMAC. What this shape
 * guarantees is narrower and still worth having: nothing about a third party is
 * here, and this JSON can be held, logged or shown while the capability itself
 * stays in memory for the thirty seconds it is worth anything.
 */
export const pvpTeamTicketResponseSchema = z.object({
  ticket: z.string().min(1),
  participantId: z.string().min(1),
  team: z.enum(['a', 'b']),
  slot: z.number().int().min(1).max(3),
  expiresAt: z.string(),
});

export type PvpTeamTicketResponse = z.infer<typeof pvpTeamTicketResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  The team match's own wire                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A second, versioned protocol for `team_3v3` - and deliberately not a wider
 * first one.
 *
 * ## Why the duel's frames were not widened
 *
 * Every shape above is keyed by SEAT, and a team participant has none. Making
 * `s` optional and adding `p` beside it would leave one decoder answering two
 * questions, and the dangerous answer is the quiet one: a frame with neither,
 * or with both, read by whichever branch ran first. It would also make the
 * duel's schemas non-strict in effect, and `rune-security.test.ts` exists
 * because a non-strict authoritative shape is how a field nobody validated gets
 * believed.
 *
 * So the duel keeps its wire byte for byte and this is a separate union. A
 * client speaks one or the other, and which one it speaks was decided by the
 * capability it presented - never by inspecting a frame.
 *
 * ## What every frame carries, and what none of them may
 *
 * Identity is EXPLICIT and canonical: team then slot, `a1`..`b3`, never a
 * position in an array. Nothing here carries an account id, an e-mail, a seat,
 * a `winnerSeat`, a ticket, a signature, an invite code, a launch, a cookie, a
 * seed, a connection, an address, a wallet or a reward. `team-wire.test.ts`
 * asserts that by KEY and by sentinel value over every frame the builders can
 * produce.
 */
/**
 * Version 2 removed the illusion fields.
 *
 * `mi` and `mo` left {@link pvpEffectsFrameSchema}; `mt` left the ship frame.
 * Both objects are strict, so this is a genuinely INCOMPATIBLE shape rather
 * than a shrinking one, in both directions:
 *
 * - a version 1 client parsing a version 2 frame finds required keys missing;
 * - a version 1 frame reaching a version 2 decoder is refused for carrying
 *   three keys nobody declared.
 *
 * Every versioned frame pins this with `z.literal`, which is what turns a
 * mismatch into a refusal at the door rather than a ship that silently stops
 * drawing halfway through a match. There is deliberately NO branch that accepts
 * both shapes: a protocol quietly speaking two versions is a protocol whose
 * bugs are invisible on one of them.
 *
 * Separate from snapshot compatibility, which reads old data this server wrote
 * and authorises nothing at all about the wire.
 */
/**
 * Version 3 says why an abandonment was an abandonment.
 *
 * {@link pvpTeamEndedSchema} gained `ab`: the cause and the chair that produced
 * it. The object is `.strict()`, so this is a genuinely INCOMPATIBLE shape in
 * both directions rather than a widening one - a version 2 decoder refuses the
 * new key, and a version 3 decoder refuses a frame without it. There is no
 * branch that accepts both, for the reason stated above.
 *
 * The version is one constant across every team frame, so `welcome` and `state`
 * move with it. That is the design and not an accident: a client speaks one
 * protocol, and "which frames of version 2 are still fine" is a question with a
 * different answer for every reader who asks it.
 *
 * The DUEL's `ended` frame gained the same fact and did NOT move, because it has
 * no version to move and its schema is not strict. The two protocols answer the
 * compatibility question differently because they were built differently; the
 * ADR records both answers rather than pretending they are one.
 */
/*
 * 4 → 5: o frame de estado passa a carregar os PROCS confirmados (V28G.I).
 *
 * É um bump de protocolo, e não de regras: o que mudou foi o conjunto de campos
 * que atravessa o fio. O frame é `.strict()`, portanto um cliente da versão 4
 * recusaria um frame da versão 5 campo a campo em vez de o ignorar - e um
 * cliente da versão 5 que recebesse um frame sem `procs` não teria como
 * distinguir "não houve nenhum" de "este servidor não sabe o que isso é".
 *
 * O frame do DUELO ganha o mesmo facto e não sobe, porque não tem versão para
 * subir e o seu schema não é estrito: lá o campo é opcional, e a ausência
 * significa exatamente "sem procs". Os dois protocolos respondem à pergunta da
 * compatibilidade de maneiras diferentes porque foram construídos de maneiras
 * diferentes - o mesmo que já acontecera com o `ended`.
 */
/*
 * 6 → 7: o Núcleo Atômico (gate V28G.J).
 *
 * Um bump de PROTOCOLO, e não de regras - embora as regras também mudem, e por
 * isso `PVP_RULES_VERSION` sobe ao lado. O que obriga este número a mover-se é o
 * conjunto de campos: `blasts` é obrigatório num frame `.strict()`, portanto um
 * cliente da versão 6 recusa-o campo a campo e um da versão 7 que recebesse um
 * frame sem ele não distinguiria "não houve explosão" de "este servidor não sabe
 * o que isso é".
 *
 * `b` no projétil e `kb` no casco são opcionais, e sozinhos não obrigariam a
 * subir. Sobem com o resto porque a versão é UMA por protocolo: "quais frames da
 * versão 6 ainda servem" é uma pergunta com uma resposta diferente para cada
 * leitor que a faz.
 */
/*
 * 7 → 8: o Arsenal Ígneo (gate V28G.L).
 *
 * Um campo, e obriga a subir pela mesma razão que `blasts` obrigou: `mis` é
 * OBRIGATÓRIO num frame `.strict()`, portanto um cliente da versão 7 recusa-o
 * campo a campo, e um da versão 8 que recebesse um frame sem ele não
 * distinguiria "não há mísseis" de "este servidor não sabe o que isso é".
 *
 * `blasts` não muda de forma: a explosão de um míssil viaja pelo mesmo canal com
 * outro `e` e com `r: 0`.
 */
/*
 * 8 → 9: o Orbe da Víbora (gate V28G.M).
 *
 * Um campo, e obriga a subir pela mesma razão que `mis` e `blasts` obrigaram:
 * `psn` é OBRIGATÓRIO num frame `.strict()`, portanto um cliente da versão 8
 * recusa-o campo a campo, e um da versão 9 que recebesse um frame sem ele não
 * distinguiria "não há veneno" de "este servidor não sabe o que isso é".
 *
 * É a primeira lista deste protocolo que descreve ESTADO em vez de eventos desde
 * o frame anterior. A razão está em {@link pvpPoisonFrameSchema}: um stack dura
 * seis segundos e tem uma marca desenhada durante esse tempo, e um canal de
 * eventos deixaria um espetador que chegasse a meio sem ver marca nenhuma.
 */
/*
 * 9 → 10: a Garra Hemorrágica (gate V28G.N).
 *
 * Três campos, e os três obrigam a subir pela mesma razão que os anteriores
 * obrigaram: `bld` e `bdt` são OBRIGATÓRIOS num frame `.strict()`, e `bd` é um
 * campo novo num comando `.strict()` que um servidor da versão 9 recusaria.
 *
 * É a primeira vez que este protocolo carrega ESTADO e EVENTO para a mesma
 * coisa, e a razão está em {@link pvpBleedBurstFrameSchema}: as marcas saírem do
 * estado é ambíguo - detonação, morte do alvo, morte do autor - e o evento é o
 * que diz qual das três aconteceu.
 *
 * Num 3x3 há uma segunda razão para `p` viajar em `bld`: com seis cascos, as
 * marcas de um aliado e as próprias estão sobre os mesmos alvos, e a autoria é o
 * que separa o que o botão conta do que ele não pode rebentar.
 */
/*
 * 10 → 11: o número vermelho da detonação (gate V28G.N).
 *
 * O mesmo campo do degrau 5 → 6 do duelo, pela mesma razão exata: `bdt.q[].d`
 * carrega o dano realmente aplicado, porque o cliente não o pode recalcular e
 * porque a arena passou a imprimi-lo.
 */
/*
 * 11 → 12: a Égide Espinhosa (gate V28G.O).
 *
 * O mesmo campo do degrau 6 → 7 do duelo, pela mesma razão exata: `rfl` carrega
 * as reflexões confirmadas, com as duas posições que o brilho e o feixe
 * precisam e com o dano realmente aplicado quando o payload redirecionado
 * aterra.
 *
 * Num 3x3 há uma razão a mais para o refletor e o destinatário viajarem os dois:
 * com seis cascos, "quem defendeu" e "quem levou" são duas cadeiras diferentes
 * que a cena tem de acertar - e um aliado no meio da linha não pode ser
 * confundido com nenhum dos dois.
 */
/*
 * 13 -> 14: a vida passa a MILLI (gate V28G.S.1).
 *
 * O mesmo degrau do duelo, pelos mesmos campos e pela mesma razao: `hp`,
 * `maxHealthMilli` e as magnitudes de vida dos eventos passam a strings
 * decimais em milesimos, e um cliente da versao 13 nao as sabe ler.
 *
 * **16 (gate V28H.L): as cartas de temporizador.**
 *
 * Duas coisas mudaram na forma, e as duas obrigam:
 *
 * - o casco ganhou `fz` e `bt` - congelado e inalvejável. Os dois são factos
 *   públicos porque mudam o que acontece a quem está do outro lado: uma nave
 *   que parou de responder, uns tiros que atravessam;
 * - a LISTA de cascos passou a poder ser mais curta do que a sala. Um casco com
 *   o Manto Espectral é **omitido** do frame de quem não é da equipa dele, e um
 *   cliente antigo leria essa ausência como uma nave que desapareceu do mapa em
 *   vez de uma que não pode ver.
 *
 * **17 (gate V28H.L): as invocações e o que as cartas desenham.**
 *
 * `sm` - a lista de invocações - e `cv` no casco, a lista do que as cartas dele
 * estão a desenhar. Um campo novo num schema estrito é uma forma nova, e um
 * cliente da versão anterior recusa-a, que é a razão de o número existir.
 *
 * **18 (gates V28H.N e V28H.O): a carga solta-se, e o círculo é passivo.**
 *
 * As mesmas três formas novas do duelo - `cr` no comando, `o` em `cv`, e `cg` e
 * `cw` no casco - pela mesma razão exacta. Os dois protocolos dizem a mesma
 * coisa com o mesmo vocabulário, e um deles a ficar para trás seria uma carta a
 * comportar-se de maneira diferente consoante o modo.
 *
 * **19 (gate V28H.Q): o nível de uma carta, na metade privada.**
 *
 * `l` em cada espaço de `ck`. A barra mostra o nível da carta, e sem o número o
 * projector preenchia `1` - o dono viu "Nível 1" numa carta de nível 6. Ele viaja
 * só para o dono da cadeira, ao lado dos arrefecimentos dela; o id da instância
 * continua a não sair do servidor, porque esse é posse.
 *
 * Um campo novo num schema estrito é uma forma nova, e um cliente da versão
 * anterior recusa-a - que é a razão de o número existir.
 *
 * **20 (gate V28H.R): o NICKNAME de cada cadeira.**
 *
 * `nick` no welcome do duelo e `n` na entrada do roster do 3x3, ao lado da
 * aparência e pela mesma razão: os dois são estáticos dentro de uma partida.
 *
 * A cor dos efeitos de carta deixou de dizer de quem eles são - ela passou a ser
 * a escolha de quem olha, e vale para todos - e a etiqueta por cima do casco é
 * uma das duas coisas que passaram a dizê-lo, com a barra de vida.
 *
 * Um campo novo num schema estrito é uma forma nova, e um cliente da versão
 * anterior recusa-a - que é a razão de o número existir.
 *
 * **21 (gate V28H.U): a bomba de carta diz-se, e a Esfera carrega-se à vista.**
 *
 * `cb` e `br` no projétil, e a letra `g` em `cv`. As três são forma nova num
 * schema estrito, e as três existem porque o que faltava era VERDADE e não
 * enfeite: sem `cb` a bomba do Catolé - que tem seis vezes o raio da Bomba
 * Atômica - era desenhada como um tiro comum; sem `br` a esfera de uma carga de
 * um toque tinha o tamanho de uma carga cheia, e o adversário não podia julgar
 * o que vinha na direcção dele; sem `g` a cena sabia que ALGUMA carta carregava
 * e não qual, portanto desenhava a corona da Lança por cima de uma esfera.
 *
 * Um cliente da versão anterior recusa a forma nova, que é a razão do número.
 *
 * **22 (gate V28H.V): o que as três cartas de MOVIMENTO desenham.**
 *
 * As mesmas três letras do duelo - `d`, `h` e `k` - pela mesma razão e no mesmo
 * campo. Aqui a do Manto tem um segundo destinatário legítimo: os ALIADOS
 * continuam a ver o casco encoberto, por decisão já tomada, e passam a vê-lo
 * como o dono o vê.
 *
 * **27 (gate V28H.X): a Marca da Caveira, vista de quem a lança.**
 *
 * A mesma letra do duelo, pela mesma razão.
 *
 * **26 (gate V28H.X): a salva do Leviatã diz que é dele.**
 *
 * A mesma letra do duelo, pela mesma razão.
 *
 * **25 (gate V28H.X): a ordem de PILOTAR.**
 *
 * A mesma ordem do duelo, pela mesma razão.
 *
 * **24 (gate V28H.X): o raio, a marca e o curto-circuito.**
 *
 * A mesma letra e os mesmos dois estados do duelo, pela mesma razão. Aqui valem
 * mais: com três cascos do outro lado, saber qual deles está marcado é a
 * diferença entre disparar ao calhas e escolher.
 *
 * **23 (gate V28H.W): o gelo, e o que ele deixa no casco.**
 *
 * As mesmas duas letras e os mesmos quatro campos do duelo, pela mesma razão.
 * Aqui eles valem mais: num 3x3 há três cascos do outro lado, e a diferença
 * entre "lento", "congelado" e "paralisado" tem de se ler de relance.
 */
export const PVP_TEAM_PROTOCOL_VERSION = 27;

const teamLetterSchema = z.enum(['a', 'b']);
const teamSlotSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
/**
 * `a1`..`b3`, checked as a pattern rather than as an enum.
 *
 * The identity is DERIVED from the team and the slot everywhere it is produced,
 * and every frame carrying one carries those two beside it - so a decoder can
 * re-derive and compare rather than trust. The pattern is the shape; the
 * agreement is what the builders and their tests check.
 */
const teamParticipantIdSchema = z.string().regex(/^[ab][123]$/, 'invalid pvp participant id');

/**
 * What only the RECIPIENT is told.
 *
 * `ack` and `gen` are private. A client reconciles its own prediction against
 * its own applied sequence, and nobody else needs one - a map of everybody's
 * sequences would publish how much input each player has in flight, which is
 * something an opponent can use and nothing a teammate needs.
 */
export const pvpTeamSelfSchema = z
  .object({
    p: teamParticipantIdSchema,
    tm: teamLetterSchema,
    sl: teamSlotSchema,
    /** Which generation of this chair's connection this socket is. */
    gen: z.number().int().min(0),
    /** The highest command sequence the room has applied for THIS participant. */
    ack: z.number().int().min(0),
    /** Os três espaços de cartas DESTA cadeira. Ver {@link pvpCardSlotsSchema}. */
    ck: pvpCardSlotsSchema,
  })
  .strict();

/** One chair as everybody may see it, before the first tick. */
export const pvpTeamRosterEntrySchema = z
  .object({
    p: teamParticipantIdSchema,
    tm: teamLetterSchema,
    sl: teamSlotSchema,
    /** Whether a socket is attached right now. */
    c: z.boolean(),
    /** Whether that person has declared themselves ready to fight. */
    r: z.boolean(),
    /** Null until that chair has presented its capability and been admitted. */
    ap: pvpAppearanceSchema.nullable(),
    /**
     * **O nickname desta cadeira** (gate V28H.R), ou `null` enquanto ninguém a
     * ocupou.
     *
     * Na ENTRADA DO ROSTER e não no quadro do casco, pela mesma razão que a
     * aparência ao lado: ele é estático dentro de uma partida, e mandá-lo a
     * cada tique seria pagar seis vezes por segundo por uma coisa que nunca
     * muda. Ele chega ao servidor pelo bilhete assinado, nunca pelo socket.
     */
    n: pvpNicknameSchema.nullable(),
    /**
     * What that hull IS (protocol version 4). Never null: a chair's ship exists
     * from the moment the room builds its simulation, so the room always knows.
     *
     * On the ROSTER ENTRY rather than on the participant frame, because it is
     * not per-tick data: it changes when the room says so and never inside a
     * tick, and repeating four integers six times a tick would be paying twenty
     * bytes eighteen hundred times a minute to say what has not changed.
     */
    cp: pvpCombatProfileSchema,
  })
  .strict();

/** One ship, mid-match, addressed by identity rather than by position. */
export const pvpTeamParticipantFrameSchema = z
  .object({
    p: teamParticipantIdSchema,
    /** Os dois canhões temporários, quando montados. Ausente = não (V28H.J). */
    sc: pvpSuperCannonsFlagSchema.optional(),
    /** Congelado por uma carta (gate V28H.L). */
    fz: pvpCardFlagSchema.optional(),
    /**
     * **Este casco está infectado com MALWARE** (gate V28H.W). Ausente = não.
     *
     * A cura que ele receber vale metade. É um estado do CASCO e não um
     * acontecimento, portanto viaja em todos os frames enquanto durar - quem
     * chegar a meio, por reconexão ou como espectador, vê o símbolo na mesma.
     */
    mw: pvpCardFlagSchema.optional(),
    /**
     * **Quanta casca de gelo desenhar**, de um a três (gate V28H.W).
     *
     * Presente só enquanto o Congelamento durar, e derivado do tempo que FALTA -
     * um por cada segundo restante, com tecto em três. Uma esfera de quatro
     * segundos começa em três camadas e desce a duas e a uma enquanto derrete, e
     * quem olha lê quanto tempo o adversário ainda está preso.
     *
     * Do que falta e não do que foi sorteado, e é deliberado: o total teria de
     * viajar como estado novo em três fronteiras - frame, instantânea e restauro -
     * para dizer uma coisa que o tempo restante já diz melhor.
     */
    ft: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    /**
     * **Paralisia TOTAL**: este congelamento também trava o ataque (V28H.W).
     *
     * Trinta por cento das vezes, decidido pela sala. Ele existe porque as duas
     * paralisias se desenham de maneiras diferentes - a total leva uma estrela de
     * gelo por cima do casco - e a cena não tem como saber qual é: a diferença
     * está na magnitude de uma ranhura que ela nunca vê.
     */
    pz: pvpCardFlagSchema.optional(),
    /**
     * **Este casco está LENTO** (gate V28H.W): metade da velocidade. Ausente = não.
     *
     * `lt` e não `sl`, que seria a letra óbvia: no 3x3 ela já é o SLOT da cadeira,
     * e duas coisas com o mesmo nome no mesmo objecto são um erro de compilação
     * num protocolo e uma confusão em qualquer leitura dele.
     */
    lt: pvpCardFlagSchema.optional(),
    /**
     * **Este casco está em CURTO-CIRCUITO** (gate V28H.X). Ausente = não.
     *
     * A cadência dele está reduzida, e isso não tem mais nenhum sinal no frame:
     * um adversário a disparar mais devagar é uma coisa que só se nota ao fim de
     * vários tiros, e um duelo decide-se antes disso. É um estado do CASCO e
     * viaja em todos os frames enquanto durar.
     */
    cc: pvpCardFlagSchema.optional(),
    /**
     * **Este casco está MARCADO pela Caveira** (gate V28H.X). Ausente = não.
     *
     * O dano que ele causa vale menos. A carta chama-se "marca", e a marca é
     * literalmente o que ela produz - sem este campo, a única carta do catálogo
     * cujo nome descreve um desenho não desenhava nada.
     */
    sk: pvpCardFlagSchema.optional(),
    bt: pvpCardFlagSchema.optional(),
    /** O que as cartas deste casco estão a desenhar. Ausente = nada. */
    cv: pvpCardVisualSchema.optional(),
    /** A carga cumprida e a força do feixe, em por cento (gate V28H.O). */
    cg: z.number().int().min(0).max(100).optional(),
    cw: z.number().int().min(0).max(100).optional(),
    tm: teamLetterSchema,
    sl: teamSlotSchema,
    x: z.number().int(),
    y: z.number().int(),
    /** Movement intent in `{-1,0,1}` per axis. The hull's heading is drawn from it. */
    mx: z.number().int().min(-1).max(1),
    my: z.number().int().min(-1).max(1),
    /** Aim, already normalised. Drawn as rotation; never re-derived. */
    ax: z.number().int(),
    ay: z.number().int(),
    /**
     * A vida deste casco, em MILLI, como STRING DECIMAL (gate V28G.S.1).
     *
     * O mesmo campo, a mesma unidade e a mesma razao que no duelo.
     */
    hp: pvpHealthMilliSchema,
    /**
     * Alive, sent rather than inferred from `hp`.
     *
     * The two agree today and the ROOM is what decides. A client computing it
     * would be a client with its own opinion about who is still in the match,
     * and that opinion would be wrong for exactly one frame every time.
     */
    alive: z.boolean(),
    /** Connected right now. A disconnected ship still exists and can be shot. */
    c: z.boolean(),
    ap: pvpAppearanceSchema.nullable(),
    e: pvpEffectsFrameSchema,
    /**
     * Tiques de EMPURRÃO que faltam a este casco (gate V28G.J).
     *
     * O mesmo campo do duelo, pela mesma razão: o preditor tem de deixar de
     * prever enquanto durar, ou a nave anda contra o bloqueio e é corrigida aos
     * saltos vinte vezes por segundo. Opcional, e a ausência diz "obedece ao
     * piloto".
     */
    kb: z.number().int().min(1).optional(),
    /**
     * **Quantas SERRAS orbitam este casco agora** (gate V28G.P).
     *
     * ## Porque a contagem viaja, em vez de o cliente a derivar
     *
     * Porque derivá-la exigiria o nível da joia, e o nível vive no perfil
     * congelado que **nunca sai do servidor**. Uma cena que o recebesse seria uma
     * cena a decorar um inventário, que é exatamente o que
     * `jewel-presentation.test.ts` proíbe. O que viaja é o FACTO - há três lâminas
     * à volta desta nave - e nunca a razão pela qual há três.
     *
     * Ausente quer dizer NENHUMA, que é o estado de todo o casco sem esta joia.
     * Um zero explícito custaria bytes em todos os frames de todas as partidas
     * para dizer o que a ausência já diz.
     */
    sw: z.number().int().min(1).max(PVP_MAX_WIRE_SAW_BLADES).optional(),
    /**
     * A FASE autoritativa da órbita, em unidades de fase (gate V28G.P).
     *
     * Presente exatamente quando {@link sw} está, e a cena deriva dela a posição e
     * a rotação de cada lâmina - determinística, e igual em todos os clientes.
     *
     * ## Porque a fase e não as posições
     *
     * Porque a posição é derivável e a fase não. Quatro pares de coordenadas por
     * casco seriam oito inteiros vinte vezes por segundo para dizer o que um
     * inteiro já diz - e seriam oito inteiros que um cliente poderia ver
     * discordarem do casco a que pertencem, se um frame os juntasse de tiques
     * diferentes.
     */
    sf: z.number().int().min(0).max(PVP_MAX_WIRE_SAW_PHASE).optional(),
    /*
     * `mt` (which enemy this ship's illusions walked towards) was here, and
     * left with the mechanic. Strict, so a frame still carrying it is refused.
     */
  })
  .strict();

/** A shot in flight, owned by an identity and never by an index. */
export const pvpTeamProjectileFrameSchema = z
  .object({
    i: z.number().int().min(0),
    x: z.number().int(),
    y: z.number().int(),
    p: teamParticipantIdSchema,
    /** The team, beside the id, so a renderer never parses one to pick a colour. */
    tm: teamLetterSchema,
    /** Este projétil é uma BOMBA (gate V28G.J). Ausente quer dizer que não é. */
    b: z.literal(true).optional(),
    /**
     * **E esta é a bomba de uma CARTA, e qual** (gate V28H.U).
     *
     * `f` é a Esfera Ígnea, `c` é o Catolé, `z` é a esfera do Congelamento e `t` é
     * a estrela da Lentidão (gate V28H.W). Ausente é um tiro comum ou a Bomba
     * Atômica da joia - que continua a marcar-se com `b` e a não mudar em nada.
     *
     * Viaja pela mesma razão que `b` viaja, e a razão está escrita em cima: a
     * cena não pode deduzi-lo. As três coisas são desenhadas de maneiras
     * diferentes, e adivinhá-lo pelo TAMANHO seria a cena a decidir a arte a
     * partir de um número de equilíbrio - e a trocá-la no dia em que alguém
     * afinasse o raio.
     */
    /**
     * **De que ESTRUTURA este tiro saiu** (gate V28H.X). Ausente = de um casco.
     *
     * §d§ é um Drone e §l§ é um Leviatã. Ver a gémea no protocolo do duelo.
     */
    sv: z.union([z.literal('d'), z.literal('l')]).optional(),
    cb: z
      .union([z.literal('f'), z.literal('c'), z.literal('z'), z.literal('t'), z.literal('s')])
      .optional(),
    /**
     * **O raio da explosão que ela vai fazer**, em unidades de mundo.
     *
     * Presente só com `cb`, e existe por uma razão que a Bomba não tem: a Esfera
     * Ígnea CRESCE com a carga. Um corpo de tamanho fixo mostraria a mesma bola
     * para um toque e para uma carga cheia, e "adversários enxergam o tamanho
     * real da esfera" foi pedido por escrito - este campo é o que o cumpre.
     *
     * O raio da EXPLOSÃO e não o do corpo: é ele que a autoridade possui, o
     * corpo desenhado é uma fracção dele, e o que se vê passa assim a ser o que
     * apanha. Nunca carrega dano, nível, carta nem dono.
     */
    br: z.number().int().min(0).optional(),
  })
  .strict();

/**
 * Um MÍSSIL num 3x3, endereçado por participante (gate V28G.L).
 *
 * A mesma forma do duelo com o vocabulário deste protocolo: a identidade em vez
 * do assento, e a equipa ao lado dela, para uma cena nunca ter de a extrair de
 * uma string. As razões pelas quais isto é uma lista à parte e pelas quais a
 * direção viaja estão em {@link pvpMissileFrameSchema}.
 */
/** Um stack de veneno num 3x3, endereçado por participante (gate V28G.M). */
export const pvpTeamPoisonFrameSchema = pvpPoisonFrameOf(teamParticipantIdSchema);
export type PvpTeamPoisonFrame = z.infer<typeof pvpTeamPoisonFrameSchema>;

/** Uma marca de sangramento num 3x3, endereçada por participante. */
export const pvpTeamBleedFrameSchema = pvpBleedFrameOf(teamParticipantIdSchema);
export type PvpTeamBleedFrame = z.infer<typeof pvpTeamBleedFrameSchema>;

/** Uma reflexão num 3x3, endereçada por participante. */
export const pvpTeamReflectFrameSchema = pvpReflectFrameOf(teamParticipantIdSchema);
export type PvpTeamReflectFrame = z.infer<typeof pvpTeamReflectFrameSchema>;

/** Uma detonação num 3x3, endereçada por participante. */
export const pvpTeamBleedBurstFrameSchema = pvpBleedBurstFrameOf(teamParticipantIdSchema);
export type PvpTeamBleedBurstFrame = z.infer<typeof pvpTeamBleedBurstFrameSchema>;

export const pvpTeamMissileFrameSchema = z
  .object({
    i: z.number().int().min(0),
    x: z.number().int(),
    y: z.number().int(),
    dx: z.number().int(),
    dy: z.number().int(),
    p: teamParticipantIdSchema,
    tm: teamLetterSchema,
  })
  .strict();

export const pvpTeamWelcomeSchema = z
  .object({
    t: z.literal('welcome'),
    v: z.literal(PVP_TEAM_PROTOCOL_VERSION),
    ruleset: z.literal('team_3v3'),
    matchId: z.string().min(1),
    /** Who YOU are. Never a list to choose from. */
    you: pvpTeamSelfSchema,
    state: z.enum(PVP_MATCH_STATES),
    mode: z.string(),
    rulesVersion: z.number().int(),
    tickMs: z.number().int(),
    arena: z
      .object({
        w: z.number().int(),
        h: z.number().int(),
        obstacles: z.array(
          z
            .object({
              x: z.number().int(),
              y: z.number().int(),
              w: z.number().int(),
              h: z.number().int(),
            })
            .strict(),
        ),
      })
      .strict(),
    /**
     * All six chairs, canonical order, whether or not they have arrived.
     *
     * The first socket may arrive before the other five, so a welcome that
     * pretended the simulation already had six ships would be a welcome that
     * lied for as long as the room took to fill. This says what is true: six
     * chairs exist, these are connected, these have declared ready, and an
     * appearance is null until its holder has presented a capability.
     */
    roster: z.array(pvpTeamRosterEntrySchema),
    shipRadius: z.number().int(),
    projectileRadius: z.number().int(),
    /** A mesma referencia normalizada do duelo, em MILLI (gate V28G.S.1). */
    maxHealthMilli: z.number().int(),
    timeLimitMs: z.number().int(),
    reconnectGraceMs: z.number().int(),
  })
  .strict();

/** Um proc num 3x3, enderecado por participante. */
export const pvpTeamProcFrameSchema = pvpProcFrameOf(teamParticipantIdSchema);
export type PvpTeamProcFrame = z.infer<typeof pvpTeamProcFrameSchema>;

/** Uma explosão num 3x3, endereçada por participante (gate V28G.J). */
export const pvpTeamBlastFrameSchema = pvpBlastFrameOf(teamParticipantIdSchema);
export type PvpTeamBlastFrame = z.infer<typeof pvpTeamBlastFrameSchema>;

export const pvpTeamStateFrameSchema = z
  .object({
    t: z.literal('state'),
    v: z.literal(PVP_TEAM_PROTOCOL_VERSION),
    /** The authoritative tick. Monotonic; the client never advances it itself. */
    k: z.number().int().min(0),
    state: z.enum(PVP_MATCH_STATES),
    /** The recipient's own identity, generation and applied sequence. */
    you: pvpTeamSelfSchema,
    parts: z.array(pvpTeamParticipantFrameSchema),
    proj: z.array(pvpTeamProjectileFrameSchema),
    /**
     * Os mísseis em voo (gate V28G.L).
     *
     * Obrigatória, como `proj` é: este frame é `.strict()` e tem versão, portanto
     * uma lista vazia diz "não há nenhum" em vez de "não sei".
     */
    mis: z.array(pvpTeamMissileFrameSchema),
    /** As invocações a existir (gate V28H.L). Ausente = nenhuma. */
    sm: z.array(pvpSummonFrameSchema).optional(),
    runes: z.array(pvpRuneFrameSchema),
    /**
     * Os procs confirmados desde o frame anterior, por ordem.
     *
     * OBRIGATORIO aqui, ao contrario do duelo: este frame e `.strict()` e tem
     * versao, portanto a forma e negociada e nao adivinhada. Uma lista vazia e
     * a resposta normal, e diz "nao houve nenhum" em vez de "nao sei".
     */
    procs: z.array(pvpTeamProcFrameSchema),
    /**
     * As explosões confirmadas desde o frame anterior, por ordem (gate V28G.J).
     *
     * OBRIGATÓRIA aqui, como `procs` é e pela mesma razão: este frame é
     * `.strict()` e tem versão, portanto uma lista vazia diz "não houve nenhuma"
     * em vez de "não sei".
     */
    blasts: z.array(pvpTeamBlastFrameSchema),
    /**
     * Os stacks de veneno ATIVOS agora (gate V28G.M).
     *
     * Estado, e não eventos desde o frame anterior - ver
     * {@link pvpPoisonFrameSchema}. Obrigatória, como todas as outras deste
     * frame: uma lista vazia diz "não há nenhum" em vez de "não sei".
     */
    psn: z.array(pvpTeamPoisonFrameSchema),
    /**
     * As marcas de sangramento ATIVAS agora (gate V28G.N).
     *
     * Estado, como `psn` - ver {@link pvpBleedFrameSchema}. Obrigatória, como
     * todas as outras deste frame.
     */
    bld: z.array(pvpTeamBleedFrameSchema),
    /**
     * As DETONAÇÕES confirmadas desde o frame anterior (gate V28G.N).
     *
     * Evento - ver {@link pvpBleedBurstFrameSchema}. Num 3x3 é ele que diz de QUEM
     * foi a ordem, o que é a diferença entre animar as marcas certas e animar as
     * de um aliado.
     */
    bdt: z.array(pvpTeamBleedBurstFrameSchema),
    /**
     * As REFLEXÕES confirmadas desde o frame anterior (gate V28G.O).
     *
     * Evento - ver {@link pvpReflectFrameSchema}. Num 3x3 é ele que diz QUEM
     * defendeu e QUEM recebe o payload virado, o que é a diferença entre acender
     * o casco certo e acender o de um aliado.
     *
     * Obrigatória, como todas as outras deste frame.
     */
    rfl: z.array(pvpTeamReflectFrameSchema),
    left: z.number().int().min(0),
  })
  .strict();

/**
 * Which chair abandoned, on which side, and whether they chose to.
 *
 * The team letter travels BESIDE the id, exactly as it does on every other team
 * frame: a client answering "was that my team-mate or theirs" must compare two
 * letters, never parse one out of an identity string. `.strict()`, so no
 * account, no ticket and no ready-made sentence can ride along.
 */
export const pvpTeamAbandonmentSchema = z
  .object({
    cause: z.enum(PVP_ABANDON_CAUSES),
    p: teamParticipantIdSchema,
    tm: teamLetterSchema,
  })
  .strict();

export const pvpTeamEndedSchema = z
  .object({
    t: z.literal('ended'),
    v: z.literal(PVP_TEAM_PROTOCOL_VERSION),
    state: z.enum(PVP_MATCH_STATES),
    /**
     * The winning TEAM, or null for a draw AND for a result nobody should trust.
     *
     * Never a seat: a team match has three people on the winning side and none
     * of them is "seat a". `reason` is what tells a draw from a service failure,
     * exactly as it does for a duel.
     */
    winnerTeam: teamLetterSchema.nullable(),
    reason: z.enum(PVP_END_REASONS),
    ticks: z.number().int().min(0),
    /**
     * Why an abandonment happened, and `null` for every other ending.
     *
     * REQUIRED, unlike the duel's, because this frame is versioned: an absent
     * key here cannot mean "an older server", so it can only mean a frame this
     * build did not write - and reading one would be reading a shape nobody
     * agreed on. The explicit `null` is the fact "not an abandonment", stated
     * rather than inferred from a missing key.
     */
    ab: pvpTeamAbandonmentSchema.nullable(),
  })
  .strict();

export const pvpTeamServerFrameSchema = z.discriminatedUnion('t', [
  pvpTeamWelcomeSchema,
  pvpTeamStateFrameSchema,
  pvpTeamEndedSchema,
  pvpErrorSchema,
  pvpClosingSchema,
]);

export type PvpTeamSelf = z.infer<typeof pvpTeamSelfSchema>;
export type PvpTeamRosterEntry = z.infer<typeof pvpTeamRosterEntrySchema>;
export type PvpTeamParticipantFrame = z.infer<typeof pvpTeamParticipantFrameSchema>;
export type PvpTeamProjectileFrame = z.infer<typeof pvpTeamProjectileFrameSchema>;
export type PvpTeamMissileFrame = z.infer<typeof pvpTeamMissileFrameSchema>;
export type PvpTeamWelcomeFrame = z.infer<typeof pvpTeamWelcomeSchema>;
export type PvpTeamStateFrame = z.infer<typeof pvpTeamStateFrameSchema>;
export type PvpTeamEndedFrame = z.infer<typeof pvpTeamEndedSchema>;
export type PvpTeamAbandonmentFrame = z.infer<typeof pvpTeamAbandonmentSchema>;
export type PvpTeamServerFrame = z.infer<typeof pvpTeamServerFrameSchema>;
